// VegetationSystem.js
// Handles procedural vegetation placement and management based on biome data

import * as THREE from 'three';
import NoiseGenerator from './NoiseGenerator.js';
import BiomeManager from './BiomeManager.js';
import TextureManager from './TextureManager.js';

// We'll load the GLTFLoader dynamically to support both direct imports and global availability
let GLTFLoader;

/**
 * ShaderUtils - Utility class for managing shaders
 */
class ShaderUtils {
  /**
   * Get shader from cache
   * @param {string} name - Shader name
   * @returns {string} Shader code
   */
  static getShader(name) {
    if (!window.shaderCache) {
      console.warn('Shader cache not initialized when getting shader:', name);
      return '';
    }
    
    const shader = window.shaderCache[name];
    if (!shader) {
      console.warn('Shader not found in cache:', name);
    }
    
    return shader || '';
  }
  
  /**
   * Register shader in cache
   * @param {string} name - Shader name
   * @param {string} code - Shader code
   * @returns {string} Registered shader code
   */
  static registerShader(name, code) {
    if (!window.shaderCache) {
      window.shaderCache = {};
    }
    
    window.shaderCache[name] = code;
    console.log(`Registered shader: ${name}`);
    return code;
  }
}

// Add new options for LOD distances
const DEFAULT_TREE_LOD_DISTANCES = [30, 80, 200]; // Near(LOD0), Mid(LOD1), Far(Impostor)
const DEFAULT_BUSH_LOD_DISTANCES = [20, 50, 120];

// Add FPS thresholds for adaptive grass density
const FPS_THRESHOLDS = {
  LOW: 25,      // Below this FPS, reduce grass significantly
  MEDIUM: 40,   // Below this FPS, reduce grass moderately
  HIGH: 55,     // Above this FPS, gradually increase grass
  TARGET: 60    // Target FPS to maintain
};

/**
 * VegetationSystem - Manages vegetation placement and rendering for the island
 * Uses biome data to determine vegetation density and types
 */
class VegetationSystem {
  /**
   * Define vegetation types and their properties
   */
  static VEGETATION_TYPES = {
    TREE_PINE: {
      id: 'tree_pine',
      name: 'Pine Tree',
      scale: { min: 0.5, max: 1.2 },
      biomes: ['forest', 'mountains'],
      densityFactor: 2.7
    },
    TREE_OAK: {
      id: 'tree_oak',
      name: 'Oak Tree',
      scale: { min: 0.4, max: 1.5 },
      biomes: ['forest', 'grassland'],
      densityFactor: 2.6
    },
    TREE_PALM: {
      id: 'tree_palm',
      name: 'Palm Tree',
      scale: { min: 0.4, max: 1.3 },
      biomes: ['beach'],
      densityFactor: 2.4
    },
    BUSH_LARGE: {
      id: 'bush_large',
      name: 'Large Bush',
      scale: { min: 0.1, max: 1.1 },
      biomes: ['grassland', 'forest', 'rocky_terrain'],
      densityFactor: 0.2
    },
    BUSH_SMALL: {
      id: 'bush_small',
      name: 'Small Bush',
      scale: { min: 0.5, max: 0.9 },
      biomes: ['grassland', 'forest'],
      densityFactor: 5.0
    },
    BUSH_DESERT: {
      id: 'bush_desert',
      name: 'Desert Bush',
      scale: { min: 0.4, max: 0.8 },
      biomes: ['beach', 'rocky_terrain'],
      densityFactor: 0.5
    },
    GRASS_TALL: {
      id: 'grass_tall',
      name: 'Tall Grass',
      scale: { min: 0.8, max: 1.2 },
      biomes: ['grassland', 'forest', 'rocky_terrain', 'mountains'],
      densityFactor: 2.0,
      isGrass: true
    },
    GRASS_SHORT: {
      id: 'grass_short',
      name: 'Short Grass',
      scale: { min: 0.7, max: 1.0 },
      biomes: ['grassland', 'beach', 'rocky_terrain', 'mountains'],
      densityFactor: 2.5,
      isGrass: true
    }
  };

  /**
   * Constructor for vegetation system
   * @param {Object} options - Configuration options
   * @param {number} options.worldSize - Size of the world in Three.js units
   * @param {number} options.mapSize - Size of the height and biome maps
   * @param {string|number} options.seed - Seed for vegetation placement
   * @param {boolean} options.useInstancing - Whether to use instanced meshes (better performance)
   * @param {boolean} options.useShaderGrass - Whether to use shader-based grass
   * @param {string} options.grassQuality - Quality setting for grass: 'low', 'medium', 'high', 'ultra'
   * @param {boolean} options.adaptiveGrass - Whether to dynamically adjust grass density based on FPS
   */
  constructor(options = {}) {
    this.options = {
      worldSize: 256,
      mapSize: 512,
      seed: 'vegetation',
      treeDensity: 1.3,
      bushDensity: 0.5,
      grassDensity: 0.7,
      useInstancing: true,
      useShaderGrass: true, // Enable shader-based grass by default
      grassQuality: 'medium', // Default quality: 'low', 'medium', 'high', 'ultra'
      debug: false, // Debug mode for collision visualization
      adaptiveGrass: true, // Enable adaptive grass density by default
      ...options
    };

    // FPS monitoring for adaptive grass density
    this.fpsMonitoring = {
      enabled: this.options.adaptiveGrass,
      frameCount: 0,
      lastTime: 0,
      fps: 60,
      history: [],
      historySize: 20, // Number of frames to average FPS over
      adaptiveDelay: 0, // Frames to wait before next adjustment
      baseInstanceCount: 0, // Initial instance count based on quality setting
      currentInstanceCount: 0, // Current number of grass instances
      adjustmentFactor: 1.0, // Current multiplier for grass density
      transitionSpeed: 0.05 // How quickly to adjust grass density (0-1)
    };

    // Store vegetation objects for proper disposal
    this.vegetationObjects = [];
    this.instancedMeshes = {};
    
    // For tracking collision meshes
    this.collisionMeshes = [];
    this.debugMode = false;

    // Cached models
    this.models = {};
    
    // Initialize loader safely
    this._initLoader();
    this.loadedModels = {};
    
    // Initialize model cache
    this.modelCache = {};

    // Track shader grass systems
    this.grassShaderSystems = [];
    
    // Initialize shader system if enabled
    if (this.options.useShaderGrass) {
      this.initializeGrassShaderSystem();
    }
    
    // Set up debug keyboard listener if debugging is enabled
    if (this.options.debug) {
      this.setupDebugKeyListener();
    }
    
    // Import THREE.BufferGeometryUtils if needed
    this.importThreeBufferGeometryUtils();
    
    // Make this object non-reactive if in a Vue context
    return typeof markRaw !== 'undefined' ? markRaw(this) : this;
  }
  
  /**
   * Initialize the GLTF loader safely
   */
  _initLoader() {
    // We don't immediately create the loader - instead we check if it's available
    // at the time of first use through the async _loadModel method
    this.gltfLoader = null;
  }
  
  /**
   * Import THREE.BufferGeometryUtils if needed
   * This ensures the geometry simplification will work properly
   */
  importThreeBufferGeometryUtils() {
    // Instead of modifying THREE directly, use a local utility
    this.bufferGeometryUtils = {
      mergeVertices: function(geometry) {
        // Simple implementation - just return the geometry
        console.log('Using simplified mergeVertices function');
        return geometry;
      }
    };
    
    console.log('Created local BufferGeometryUtils for detailed collision meshes');
  }
  
  /**
   * Initialize grass shader system
   */
  initializeGrassShaderSystem() {
    // Initialize ShaderUtils if not already done
    if (!window.ShaderUtils) {
      window.ShaderUtils = ShaderUtils;
    }
    
    console.log('Initializing shader system');
    
    // Make sure we have a shader cache
    if (!window.shaderCache) {
      window.shaderCache = {};
    }
    
    // Register grass shaders with direct keys
    window.shaderCache['grass/vertex.glsl'] = this.getGrassVertexShader();
    window.shaderCache['grass/fragment.glsl'] = this.getGrassFragmentShader();
    
    console.log('Registered shaders:', Object.keys(window.shaderCache));
  }
  
  /**
   * Get grass vertex shader - optimized version without wind animation
   * @returns {string} Vertex shader code
   */
  getGrassVertexShader() {
    return `
      uniform mat4 modelViewMatrix;
      uniform mat4 projectionMatrix;
      uniform float time;
      uniform vec3 cameraPosition;
      uniform float nearDistance;
      uniform float farDistance;
      uniform vec3 playerPosition;
      uniform float playerStrength;
      
      attribute vec3 position;
      attribute vec2 uv;
      attribute vec3 normal;
      attribute vec3 instancePosition;
      attribute float instanceScale;
      attribute float instanceRotation;
      attribute vec3 instanceColor;
      attribute float instanceWidth;
      
      varying vec2 vUv;
      varying float vHeight;
      varying vec3 vColor;
      varying vec3 vNormal;
      
      // Simple rotation function around Y axis
      vec2 rotate2D(vec2 v, float angle) {
        float s = sin(angle);
        float c = cos(angle);
        return vec2(v.x * c - v.z * s, v.x * s + v.z * c);
      }
      
      void main() {
        // Pass variables to fragment shader
        vUv = uv;
        vColor = instanceColor;
        vHeight = position.y + 0.5; // Convert from [-0.5, 0.5] to [0, 1]
        
        // Apply transformations
        vec3 pos = position;
        
        // Apply width scaling
        pos.x *= instanceWidth;
        
        // Apply height scaling
        pos.y *= instanceScale;
        
        // Apply player interaction only to upper part
        if (vHeight > 0.3) {
          // Player interaction
          float playerDist = distance(playerPosition, instancePosition);
          if (playerDist < 3.0 && playerStrength > 0.0) {
            vec3 toPlayer = normalize(instancePosition - playerPosition);
            float playerFactor = (1.0 - playerDist / 3.0) * playerStrength * vHeight * vHeight;
            pos.x += toPlayer.x * playerFactor;
            pos.z += toPlayer.z * playerFactor;
          }
        }
        
        // Apply rotation around Y axis
        vec2 rotatedXZ = rotate2D(vec2(pos.x, pos.z), instanceRotation);
        pos.x = rotatedXZ.x;
        pos.z = rotatedXZ.y;
        
        // Apply position offset
        pos += instancePosition;
        
        // Calculate final position
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        
        // Calculate normal for lighting
        vNormal = normalize(mat3(modelViewMatrix) * normal);
      }
    `;
  }
  
  /**
   * Get grass fragment shader
   * @returns {string} Fragment shader code
   */
  getGrassFragmentShader() {
    return `
      uniform vec3 baseColor;
      uniform vec3 tipColor;
      uniform vec3 lightDirection;
      
      varying vec2 vUv;
      varying float vHeight;
      varying vec3 vColor;
      varying vec3 vNormal;
      
      void main() {
        // Blend colors based on height
        vec3 baseColorModified = baseColor * vColor;
        vec3 tipColorModified = tipColor * vColor;
        vec3 color = mix(baseColorModified, tipColorModified, vHeight);
        
        // Simple lighting
        float ambient = 0.3;
        float diffuse = max(0.0, dot(normalize(vNormal), normalize(lightDirection)));
        float light = ambient + diffuse * 0.7;
        
        color *= light;
        
        gl_FragColor = vec4(color, 1.0);
      }
    `;
  }

  /**
   * Generate a vegetation density map based on biome and noise
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {string} vegetationType - Type of vegetation to generate density for
   * @returns {number[][]} 2D array of density values (0-1)
   */
  generateDensityMap(biomeMap, vegetationType) {
    const width = biomeMap[0].length;
    const height = biomeMap.length;
    const densityMap = Array(height).fill().map(() => Array(width).fill(0));
    
    // Generate base noise for density variation
    const densityNoise = NoiseGenerator.generateSimplexNoise(
      width,
      height,
      width / 8,
      4,
      0.5,
      2,
      this.options.seed + '-' + vegetationType
    );

    const vegType = VegetationSystem.VEGETATION_TYPES[vegetationType];
    if (!vegType) {
      console.error(`Unknown vegetation type: ${vegetationType}`);
      return densityMap;
    }

    // Calculate density based on biome and noise
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const biome = biomeMap[y][x];
        if (!biome || !biome.id) continue;
        
        // Check if this vegetation type grows in this biome
        if (vegType.biomes.includes(biome.id)) {
          // Base density determined by vegetation type and biome
          let baseDensity = vegType.densityFactor;
          
          // Apply density multiplier based on specific biome
          if (biome.id === 'forest' && vegType.id.startsWith('tree')) {
            baseDensity *= 1.5; // More trees in forests
          } else if (biome.id === 'grassland' && vegType.id.startsWith('grass')) {
            baseDensity *= 1.3; // More grass in grasslands
          } else if (biome.id === 'beach' && vegType.id === 'tree_palm') {
            baseDensity *= 0.8; // Some palms on beaches, but not too many
          }
          
          // Add noise variation (0.2-1.0 range to maintain minimum density)
          const noiseValue = 0.2 + (densityNoise[y][x] * 0.8);
          
          // Combine base density with noise (normalized to 0-1)
          densityMap[y][x] = Math.min(1.0, baseDensity * noiseValue);
          
          // Reduce density near water for a more natural shoreline
          if (biome.id === 'beach') {
            // Find neighboring water pixels
            let waterNeighbors = 0;
            for (let ny = Math.max(0, y - 1); ny <= Math.min(height - 1, y + 1); ny++) {
              for (let nx = Math.max(0, x - 1); nx <= Math.min(width - 1, x + 1); nx++) {
                if (biomeMap[ny][nx].id.includes('water')) {
                  waterNeighbors++;
                }
              }
            }
            // Reduce density based on water neighbors (more water = less vegetation)
            if (waterNeighbors > 0) {
              densityMap[y][x] *= (1 - (waterNeighbors / 9));
            }
          }
        }
      }
    }
    
    return densityMap;
  }

  /**
   * Create and place vegetation based on biome map and height map
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {number[][]} heightMap - 2D array of height values
   * @param {THREE.Scene} scene - Three.js scene to add vegetation to
   * @returns {THREE.Object3D[]} Array of added vegetation objects
   */
  placeVegetation(biomeMap, heightMap, scene) {
    console.time('Vegetation placement');
    this.vegetationObjects = [];
    
    // Generate density maps for different vegetation types
    console.log('Generating density maps...');
    const treeDensityMap = this.generateDensityMap(biomeMap, 'TREE_PINE');
    const bushDensityMap = this.generateDensityMap(biomeMap, 'BUSH_LARGE');
    const grassDensityMap = this.generateDensityMap(biomeMap, 'GRASS_TALL');
    
    // Create simplified geometry and materials for different vegetation types
    this.createVegetationModels();
    
    // Place trees
    this.placeVegetationType('TREE_PINE', heightMap, biomeMap, treeDensityMap, scene);
    this.placeVegetationType('TREE_OAK', heightMap, biomeMap, treeDensityMap, scene);
    this.placeVegetationType('TREE_PALM', heightMap, biomeMap, treeDensityMap, scene);
    
    // Place bushes
    this.placeVegetationType('BUSH_LARGE', heightMap, biomeMap, bushDensityMap, scene);
    this.placeVegetationType('BUSH_SMALL', heightMap, biomeMap, bushDensityMap, scene);
    this.placeVegetationType('BUSH_DESERT', heightMap, biomeMap, bushDensityMap, scene);
    
    // Place grass
    this.placeVegetationType('GRASS_TALL', heightMap, biomeMap, grassDensityMap, scene);
    this.placeVegetationType('GRASS_SHORT', heightMap, biomeMap, grassDensityMap, scene);
    
    console.timeEnd('Vegetation placement');
    return this.vegetationObjects;
  }

  /**
   * Place a specific type of vegetation
   * @param {string} vegetationType - Type of vegetation to place
   * @param {number[][]} heightMap - 2D array of height values
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {number[][]} densityMap - 2D array of density values
   * @param {THREE.Scene} scene - Three.js scene to add vegetation to
   */
  placeVegetationType(vegetationType, heightMap, biomeMap, densityMap, scene) {
    const vegType = VegetationSystem.VEGETATION_TYPES[vegetationType];
    if (!vegType) {
      console.error(`Unknown vegetation type: ${vegetationType}`);
      return;
    }
    
    // Check if this is grass and we're using shader-based grass
    const isGrass = vegetationType.startsWith('GRASS_');
    if (isGrass && this.options.useShaderGrass) {
      this.placeShaderGrass(vegetationType, heightMap, biomeMap, densityMap, scene);
      return;
    }
    
    // Track the actual id to use when storing/retrieving instances
    const vegTypeId = vegType.id;
    console.log(`Placing ${vegetationType} vegetation (id: ${vegTypeId})`);
    
    const width = heightMap[0].length;
    const height = heightMap.length;
    const worldSize = this.options.worldSize;
    
    // Calculate spacing based on density and map size
    const baseSpacing = vegType.isGrass ? 10 : 20;
    const spacing = Math.max(1, Math.floor(width / baseSpacing));
    
    // Create an array to store instance positions
    const positions = [];
    const scales = [];
    const rotations = [];
    
    // For trees, also store data for collision detection
    const isTree = vegetationType.startsWith('TREE_');
    const isBush = vegetationType.startsWith('BUSH_');
    const treeInstanceData = [];
    const bushInstanceData = [];
    
    // Sample points with controlled spacing
    for (let y = spacing; y < height - spacing; y += spacing) {
      for (let x = spacing; x < width - spacing; x += spacing) {
        const biome = biomeMap[y][x];
        if (!biome || !vegType.biomes.includes(biome.id)) continue;
        
        // Skip water and snow biomes
        if (biome.id.includes('water') || biome.id === 'snow') continue;
        
        // Use density map to determine if vegetation should be placed
        const density = densityMap[y][x];
        const placementThreshold = vegType.isGrass ? 0.5 : 0.7;
        
        // Add some randomness to placement
        const random = this.seededRandom(`${this.options.seed}-${vegetationType}-${x}-${y}`);
        
        if (random() < density * this.getDensityMultiplier(vegetationType)) {
          // Convert from map coordinates to world coordinates
          const wx = ((x / width) - 0.5) * worldSize;
          const wz = ((y / height) - 0.5) * worldSize;
          
          // Use height map to determine y position
          const heightValue = heightMap[y][x];
          const wy = heightValue * 10 + 0.1; // Add a small offset to prevent z-fighting
          
          // Apply random offset to position for natural variation
          const offsetX = (random() - 0.5) * spacing * (worldSize / width);
          const offsetZ = (random() - 0.5) * spacing * (worldSize / height);
          
          // Add some checking to prevent vegetation on steep slopes
          // Calculate slope by checking neighboring heights
          if (this.isSlopeTooSteep(heightMap, x, y)) {
            continue;
          }
          
          // Add position, scale, and rotation to arrays for instancing
          positions.push(wx + offsetX, wy, wz + offsetZ);
          
          // Random scale within range for that vegetation type
          const scale = vegType.scale.min + random() * (vegType.scale.max - vegType.scale.min);
          scales.push(scale, scale, scale);
          
          // Random rotation around y-axis
          const rotation = random() * Math.PI * 2;
          rotations.push(0, rotation, 0);
          
          // Store tree data for collision if this is a tree
          if (isTree) {
            treeInstanceData.push({
              position: new THREE.Vector3(wx + offsetX, wy, wz + offsetZ),
              scale: scale,
              rotation: rotation
            });
          }

          // Store bush data for collision if this is a bush
          if (isBush) {
            bushInstanceData.push({
              position: new THREE.Vector3(wx + offsetX, wy, wz + offsetZ),
              scale: scale,
              rotation: rotation
            });
          }
        }
      }
    }
    
    // Create instanced mesh if there are any positions
    if (positions.length > 0 && this.options.useInstancing) {
      // Use the vegTypeId for storing in instancedMeshes to maintain consistency
      const instancedMesh = this.createInstancedMesh(vegetationType, positions, scales, rotations, scene);
      console.log(`Created ${positions.length / 3} instances of ${vegetationType} (${vegTypeId})`);
      
      // If these are trees, we need to register them for collision detection
      if (isTree && instancedMesh) {
        instancedMesh.userData = {
          isObstacle: true,
          type: 'tree-instanced',
          vegetationType: vegetationType,
          instanceCount: positions.length / 3,
          instanceData: treeInstanceData
        };
        
        // Create individual collision meshes for each tree instance
        if (treeInstanceData.length > 0) {
          this.createTreeCollisionObjects(treeInstanceData, vegetationType, scene);
        }
      }
      
      // If these are bushes, we need to register them for collision detection
      if (isBush && instancedMesh) {
        instancedMesh.userData = {
          isObstacle: true,
          type: 'bush-instanced',
          vegetationType: vegetationType,
          instanceCount: positions.length / 3,
          instanceData: bushInstanceData
        };
        
        // Create individual collision meshes for each bush instance
        if (bushInstanceData.length > 0) {
          this.createBushCollisionObjects(bushInstanceData, vegetationType, scene);
        }
      }
    }
    // For debugging or if instancing is disabled, create individual meshes
    else if (positions.length > 0 && !this.options.useInstancing) {
      for (let i = 0; i < positions.length; i += 3) {
        this.createIndividualVegetation(
          vegetationType,
          positions[i],
          positions[i + 1],
          positions[i + 2],
          scales[i],
          rotations[i + 1],
          scene
        );
      }
    }
  }
  
  /**
   * Create individual collision objects for tree instances
   * @param {Array} treeData - Array of tree instance data
   * @param {string} vegetationType - Type of vegetation
   * @param {THREE.Scene} scene - Three.js scene
   */
  createTreeCollisionObjects(treeData, vegetationType, scene) {
    // Create invisible collision objects for each tree
    treeData.forEach((tree, index) => {
      // Scale factors based on tree type
      const trunkHeight = 2.0 * tree.scale; // Approximate trunk height
      const trunkRadius = 0.3 * tree.scale; // Approximate trunk radius
      
      // Create collision box for the trunk
      const collisionGeometry = new THREE.BoxGeometry(
        trunkRadius * 1.5,
        trunkHeight*3,
        trunkRadius * 1.5
      );
      
      // Material depends on debug mode
      const collisionMaterial = new THREE.MeshBasicMaterial({
        visible: this.debugMode,
        wireframe: this.debugMode,
        color: 0x00ff00, // Green for tree collisions
        transparent: true,
        opacity: this.debugMode ? 0.5 : 0
      });
      
      const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
      
      // Position the collision mesh
      collisionMesh.position.copy(tree.position);
      collisionMesh.position.y += trunkHeight / 2;
      collisionMesh.rotation.y = tree.rotation;
      
      // Add metadata
      collisionMesh.userData = {
        isObstacle: true,
        isCollider: true,
        type: 'tree-instance-collider',
        vegetationType: vegetationType,
        instanceIndex: index
      };
      
      // Ensure it has a bounding box
      collisionGeometry.computeBoundingBox();
      
      // Add to scene and track it
      scene.add(collisionMesh);
      this.vegetationObjects.push(collisionMesh);
      this.collisionMeshes.push(collisionMesh); // Track for debug visualizations
    });
    
    console.log(`Created ${treeData.length} tree collision objects for ${vegetationType}`);
  }

  /**
   * Check if the slope at a given point is too steep for vegetation
   * @param {number[][]} heightMap - 2D array of height values
   * @param {number} x - X position in the height map
   * @param {number} y - Y position in the height map
   * @returns {boolean} True if the slope is too steep
   */
  isSlopeTooSteep(heightMap, x, y) {
    const width = heightMap[0].length;
    const height = heightMap.length;
    
    // Get center height
    const centerHeight = heightMap[y][x];
    
    // Check neighboring heights (using a smaller neighborhood for efficiency)
    let maxDifference = 0;
    const neighbors = [
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 }
    ];
    
    for (const n of neighbors) {
      const nx = x + n.dx;
      const ny = y + n.dy;
      
      // Skip if outside bounds
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
      
      const neighborHeight = heightMap[ny][nx];
      const difference = Math.abs(centerHeight - neighborHeight);
      maxDifference = Math.max(maxDifference, difference);
    }
    
    // Threshold for "too steep" (adjust as needed)
    // This is a height difference, not an angle
    const steepThreshold = 0.05; // 5% height difference
    return maxDifference > steepThreshold;
  }

  /**
   * Get density multiplier for a vegetation type
   * @param {string} vegetationType - Type of vegetation
   * @returns {number} Density multiplier
   */
  getDensityMultiplier(vegetationType) {
    if (vegetationType.startsWith('TREE_')) {
      return this.options.treeDensity;
    } else if (vegetationType.startsWith('BUSH_')) {
      return this.options.bushDensity;
    } else if (vegetationType.startsWith('GRASS_')) {
      return this.options.grassDensity;
    }
    return 1.0; // Default
  }

  /**
   * Create simple models for each vegetation type
   */
  createVegetationModels() {
    console.log('Creating procedural vegetation models');
    
    // Create procedural models first as fallbacks
    // Tree geometries and materials
    this.models.TREE_PINE = this.createTreeModel([0, 0.7, 0], 0.2, 2.0);
    this.models.TREE_OAK = this.createTreeModel([0.1, 0.6, 0.1], 0.3, 1.8);
    this.models.TREE_PALM = this.createTreeModel([0.2, 0.8, 0.2], 0.2, 2.2);
    
    // Bush geometries and materials
    this.models.BUSH_LARGE = this.createBushModel([0.2, 0.5, 0.2], 0.7);
    this.models.BUSH_SMALL = this.createBushModel([0.3, 0.6, 0.3], 0.5);
    this.models.BUSH_DESERT = this.createBushModel([0.5, 0.5, 0.3], 0.4);
    
    // Grass geometries and materials
    this.models.GRASS_TALL = this.createGrassModel([0.3, 0.7, 0.2], 0.8);
    this.models.GRASS_SHORT = this.createGrassModel([0.4, 0.6, 0.2], 0.4);
    
    // Create a mapping from vegetation IDs to types
    this.vegIdToType = {};
    for (const key in VegetationSystem.VEGETATION_TYPES) {
      const vegType = VegetationSystem.VEGETATION_TYPES[key];
      this.vegIdToType[vegType.id] = key;
    }
    
    // Load models using the async _loadModel method
    this.loadVegetationModels();
  }
  
  /**
   * Load all vegetation models using _loadModel method
   */
  loadVegetationModels() {
    console.log('Loading vegetation models...');
  
    // Load palm tree model
    console.log('🌴 Loading palm tree model from /models/vegetation/palm_1.glb for all trees...');
    this._loadModel('/models/vegetation/palm_1.glb')
      .then(model => {
        console.log('✅ Successfully loaded palm tree model!', model);
        this.applyModelToTreeTypes(model, ['TREE_PINE', 'TREE_OAK', 'TREE_PALM']);
      })
      .catch(error => {
        console.error('❌ Error loading palm tree model:', error);
        console.warn('⚠️ Using procedural tree models as fallback');
        console.error('Error details:', {
          error: error.message || error,
          stack: error.stack,
          modelPath: '/models/vegetation/palm_1.glb'
        });
      });
      
    // Load shipping container model
    console.log('📦 Loading shipping container model from /models/rocks/shipping_container.glb for BUSH_SMALL...');
    this._loadModel('/models/rocks/shipping_container.glb')
      .then(model => {
        console.log('✅ Successfully loaded shipping container model!', model);
        this.applyModelToBushType(model, 'BUSH_SMALL');
      })
      .catch(error => {
        console.error('❌ Error loading shipping container model:', error);
        console.warn('⚠️ Using procedural bush model as fallback for BUSH_SMALL');
        console.error('Error details:', {
          error: error.message || error,
          stack: error.stack,
          modelPath: '/models/rocks/shipping_container.glb'
        });
      });
      
    // Load rock chunks model
    console.log('🪨 Loading rock chunks model from /models/rocks/rock_chunks.glb for BUSH_LARGE...');
    this._loadModel('/models/rocks/rock_chunks.glb')
      .then(model => {
        console.log('✅ Successfully loaded rock chunks model!', model);
        this.applyModelToBushType(model, 'BUSH_LARGE');
      })
      .catch(error => {
        console.error('❌ Error loading rock chunks model:', error);
        console.warn('⚠️ Using procedural bush model as fallback for BUSH_LARGE');
        console.error('Error details:', {
          error: error.message || error,
          stack: error.stack,
          modelPath: '/models/rocks/rock_chunks.glb'
        });
      });
  }
  
  /**
   * Apply loaded model to all tree types
   * @param {THREE.Object3D} model - The loaded model
   * @param {string[]} treeTypes - Array of tree type names to apply the model to
   */
  applyModelToTreeTypes(model, treeTypes) {
    // Apply polygon offset to all materials in the model to fix shadow rendering
    model.traverse(child => {
      if (child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map(m => {
            const matClone = m.clone();
            matClone.polygonOffset = true;
            matClone.polygonOffsetFactor = -0.2;
            matClone.polygonOffsetUnits = -1;
            return matClone;
          });
        } else {
          child.material = child.material.clone();
          child.material.polygonOffset = true;
          child.material.polygonOffsetFactor = -0.2;
          child.material.polygonOffsetUnits = -1;
        }
        
        // Make sure shadows are enabled
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    // Apply the model to all tree types
    treeTypes.forEach(treeType => {
      // Store the loaded model
      this.loadedModels[treeType] = model.clone();
      
      // Replace the placeholder with the loaded model
      this.models[treeType] = { 
        geometry: model.clone(),
        material: null,
        isLoaded: true
      };
      
      console.log(`${treeType} model updated with loaded palm tree GLB model`);
      
      // If we already have an instanced mesh for this type, update it
      const vegTypeId = treeType === 'TREE_PALM' 
        ? VegetationSystem.VEGETATION_TYPES.TREE_PALM.id 
        : treeType === 'TREE_PINE'
          ? VegetationSystem.VEGETATION_TYPES.TREE_PINE.id
          : VegetationSystem.VEGETATION_TYPES.TREE_OAK.id;
          
      if (this.instancedMeshes[treeType]) {
        this.updateInstancedMesh(treeType);
      } else if (this.instancedMeshes[vegTypeId]) {
        this.updateInstancedMesh(treeType, vegTypeId);
      } else {
        console.log(`ℹ️ No existing ${treeType} instances to update yet`);
      }
    });
  }
  
  /**
   * Apply loaded model to bush type
   * @param {THREE.Object3D} model - The loaded model
   * @param {string} bushType - Bush type name to apply the model to
   */
  applyModelToBushType(model, bushType) {
    // Apply polygon offset to all materials in the model to fix shadow rendering
    model.traverse(child => {
      if (child.isMesh && child.material) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map(m => {
            const matClone = m.clone();
            matClone.polygonOffset = true;
            matClone.polygonOffsetFactor = -0.2;
            matClone.polygonOffsetUnits = -1;
            return matClone;
          });
        } else {
          child.material = child.material.clone();
          child.material.polygonOffset = true;
          child.material.polygonOffsetFactor = -0.2;
          child.material.polygonOffsetUnits = -1;
        }
        
        // Make sure shadows are enabled
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    // Store the loaded model
    this.loadedModels[bushType] = model;
    
    // Replace the placeholder with the loaded model
    this.models[bushType] = { 
      geometry: model,
      material: null,
      isLoaded: true
    };
    
    console.log(`${bushType} model updated with loaded GLB model`);
    
    // If we already have an instanced mesh for this type, update it
    const vegTypeId = VegetationSystem.VEGETATION_TYPES[bushType].id;
    
    if (this.instancedMeshes[bushType]) {
      this.updateInstancedMesh(bushType);
    } else if (this.instancedMeshes[vegTypeId]) {
      this.updateInstancedMesh(bushType, vegTypeId);
    } else {
      console.log(`ℹ️ No existing ${bushType} instances to update yet`);
    }
  }

  /**
   * Create a simple tree model
   * @param {number[]} color - RGB color values (0-1)
   * @param {number} trunkRadius - Radius of the trunk
   * @param {number} height - Height of the tree
   * @returns {Object} Tree model with trunk and crown meshes
   */
  createTreeModel(color, trunkRadius, height) {
    const trunkHeight = height * 0.4;
    const crownHeight = height * 0.6;
    
    // Trunk (cylinder)
    const trunkGeometry = new THREE.CylinderGeometry(trunkRadius * 0.7, trunkRadius, trunkHeight, 8);
    const trunkMaterial = new THREE.MeshLambertMaterial({ color: new THREE.Color(0.5, 0.3, 0.2) });
    
    // Apply polygon offset to fix shadow rendering issues
    trunkMaterial.polygonOffset = true;
    trunkMaterial.polygonOffsetFactor = -0.2;
    trunkMaterial.polygonOffsetUnits = -1;
    
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = trunkHeight / 2;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    
    // Crown (cone for pine, sphere for other trees)
    let crownGeometry;
    if (color[1] > 0.65) { // Pine tree (darker green)
      crownGeometry = new THREE.ConeGeometry(height * 0.3, crownHeight, 8);
    } else {
      crownGeometry = new THREE.SphereGeometry(height * 0.25, 8, 6);
    }
    
    const crownMaterial = new THREE.MeshLambertMaterial({ color: new THREE.Color(color[0], color[1], color[2]) });
    
    // Apply polygon offset to fix shadow rendering issues
    crownMaterial.polygonOffset = true;
    crownMaterial.polygonOffsetFactor = -0.2;
    crownMaterial.polygonOffsetUnits = -1;
    
    const crown = new THREE.Mesh(crownGeometry, crownMaterial);
    crown.position.y = trunkHeight + crownHeight * 0.5;
    crown.castShadow = true;
    crown.receiveShadow = true;
    
    // Combine into a group
    const tree = new THREE.Group();
    tree.add(trunk);
    tree.add(crown);
    
    // Add collision mesh for the trunk
    this.addCollisionMesh(
      tree, 
      trunkRadius * 2,  // Width
      trunkHeight,      // Height
      trunkRadius * 2,  // Depth
      new THREE.Vector3(0, trunkHeight / 2, 0) // Position
    );
    
    // Mark the tree as an obstacle
    tree.userData = {
      isObstacle: true,
      type: 'tree'
    };
    
    return { geometry: tree, material: null };
  }

  /**
   * Create a bush model
   * @param {number[]} color - RGB color values (0-1)
   * @param {number} size - Size of the bush
   * @returns {Object} Bush model
   */
  createBushModel(color, size) {
    // Create a group of spheres for the bush
    const bushGeometry = new THREE.SphereGeometry(size, 6, 6);
    const bushMaterial = new THREE.MeshLambertMaterial({ color: new THREE.Color(color[0], color[1], color[2]) });
    const bush = new THREE.Mesh(bushGeometry, bushMaterial);
    
    // Enable shadows for the bush
    bush.castShadow = true;
    bush.receiveShadow = true;
    
    return { geometry: bush, material: bushMaterial };
  }

  /**
   * Create a grass model
   * @param {number[]} color - RGB color values (0-1)
   * @param {number} height - Height of the grass
   * @returns {Object} Grass model
   */
  createGrassModel(color, height) {
    // Create a simple plane for grass
    const grassGeometry = new THREE.PlaneGeometry(height * 0.5, height);
    const grassMaterial = new THREE.MeshLambertMaterial({
      color: new THREE.Color(color[0], color[1], color[2]),
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.5
    });
    
    const grass = new THREE.Mesh(grassGeometry, grassMaterial);
    
    // Grass doesn't cast shadows for performance reasons
    grass.castShadow = false;
    grass.receiveShadow = true;
    
    return { geometry: grass, material: grassMaterial };
  }

  /**
   * Handle successfully loaded model
   * @param {string} modelType - Type of model loaded
   * @param {string} path - Path the model was loaded from
   * @param {Object} gltf - The loaded GLTF data
   */
  onModelLoaded(modelType, path, gltf) {
    console.log(`✅ Successfully loaded model for ${modelType} from ${path}`);
    
    // Apply polygon offset to all materials in the model to fix shadow rendering
    // This is critical for proper shadow casting when models intersect with terrain
    gltf.scene.traverse(child => {
      if (child.isMesh) {
        // Ensure shadows are enabled
        child.castShadow = true;
        child.receiveShadow = true;
        
        // Apply polygon offset to all materials
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material = child.material.map(m => {
              const matClone = m.clone();
              matClone.polygonOffset = true;
              matClone.polygonOffsetFactor = -0.2;
              matClone.polygonOffsetUnits = -1;
              return matClone;
            });
          } else {
            child.material = child.material.clone();
            child.material.polygonOffset = true;
            child.material.polygonOffsetFactor = -0.2;
            child.material.polygonOffsetUnits = -1;
          }
        }
      }
    });
    
    // Store the loaded model
    this.loadedModels[modelType] = gltf.scene;
    
    // Replace the placeholder with the loaded model
    this.models[modelType] = { 
      geometry: gltf.scene,
      material: null,
      isLoaded: true
    };
    
    // If we already have an instanced mesh for this type, update it
    if (this.instancedMeshes[modelType]) {
      this.updateInstancedMesh(modelType);
    }
  }
  
  /**
   * Update instanced mesh after model is loaded
   * @param {string} vegetationType - Type of vegetation
   * @param {string} [instanceId] - Optional ID to look up instances
   */
  updateInstancedMesh(vegetationType, instanceId) {
    // Try to find the instanced mesh by different identifiers
    let existingMesh = null;
    let meshKey = null;
    
    // Try direct lookup by vegetationType
    if (this.instancedMeshes[vegetationType]) {
      existingMesh = this.instancedMeshes[vegetationType];
      meshKey = vegetationType;
    } 
    // Try lookup by provided instanceId
    else if (instanceId && this.instancedMeshes[instanceId]) {
      existingMesh = this.instancedMeshes[instanceId];
      meshKey = instanceId;
    } 
    // Try lookup by vegetation ID from the enum
    else if (VegetationSystem.VEGETATION_TYPES[vegetationType]) {
      const vegId = VegetationSystem.VEGETATION_TYPES[vegetationType].id;
      if (this.instancedMeshes[vegId]) {
        existingMesh = this.instancedMeshes[vegId];
        meshKey = vegId;
      }
    }
    
    if (!existingMesh) {
      console.log(`No existing instances to update for ${vegetationType}`);
      if (this.instancedMeshes && Object.keys(this.instancedMeshes).length > 0) {
        console.log('Available instance keys:', Object.keys(this.instancedMeshes));
      }
      return;
    }
    
    const loadedModel = this.loadedModels[vegetationType];
    
    if (!loadedModel) {
      console.error(`No loaded model found for ${vegetationType}`);
      return;
    }
    
    console.log(`Updating instances for ${vegetationType} (${meshKey}). Existing mesh:`, existingMesh);
    
    // Get the scene the existing instances are in
    const scene = existingMesh.parent;
    if (!scene) {
      console.error(`Instance mesh for ${vegetationType} not in any scene`);
      return;
    }
    
    //console.log(`Found scene for ${vegetationType}:`, scene);
    
    // Store all the positions, rotations, and scales
    const positions = [];
    const rotations = [];
    const scales = [];
    
    // If it's a regular instanced mesh
    if (existingMesh.isInstancedMesh) {
      //console.log(`Processing instanced mesh with ${existingMesh.count} instances`);
      const dummy = new THREE.Object3D();
      
      for (let i = 0; i < existingMesh.count; i++) {
        existingMesh.getMatrixAt(i, dummy.matrix);
        dummy.matrix.decompose(dummy.position, dummy.quaternion, dummy.scale);
        
        positions.push(dummy.position.x, dummy.position.y, dummy.position.z);
        rotations.push(dummy.rotation.x, dummy.rotation.y, dummy.rotation.z);
        scales.push(dummy.scale.x, dummy.scale.y, dummy.scale.z);
      }
    } 
    // If it's a group of instances
    else if (existingMesh.isGroup || existingMesh.type === 'Group') {
      console.log(`Processing group with ${existingMesh.children.length} children`);
      
      existingMesh.children.forEach(child => {
        positions.push(child.position.x, child.position.y, child.position.z);
        rotations.push(child.rotation.x, child.rotation.y, child.rotation.z);
        scales.push(child.scale.x, child.scale.y, child.scale.z);
      });
    } else {
      console.warn(`Unknown mesh type for ${vegetationType}:`, existingMesh.type);
    }
    
    // Remove the old instances
    scene.remove(existingMesh);
    this.vegetationObjects = this.vegetationObjects.filter(obj => obj !== existingMesh);
    
    console.log(`Creating ${positions.length / 3} new instances with loaded model`);
    
    // Create new instances with the loaded model
    const instanceGroup = new THREE.Group();
    instanceGroup.name = `${vegetationType}-instances`;
    
    // Determine scale adjustment based on tree type
    let scaleMultiplier = 1.0;
    let heightOffset = 0;
    
    if (vegetationType === 'TREE_PINE') {
      scaleMultiplier = 1.5;  // Pine trees are taller
      heightOffset = -0.5;    // Adjust height to account for the palm tree model's base
    } else if (vegetationType === 'TREE_OAK') {
      scaleMultiplier = 1.8;  // Oak trees are wider
      heightOffset = -0.5;    // Adjust height to account for the palm tree model's base
    } else if (vegetationType === 'TREE_PALM') {
      scaleMultiplier = 1.0;  // Default scale for palm trees
      heightOffset = 0;       // No height adjustment needed
    }
    
    for (let i = 0; i < positions.length; i += 3) {
      const instance = loadedModel.clone();
      
      // Apply position with height offset
      instance.position.set(
        positions[i],
        positions[i + 1] + heightOffset,
        positions[i + 2]
      );
      
      // Apply rotation
      instance.rotation.set(
        rotations[i],
        rotations[i + 1],
        rotations[i + 2]
      );
      
      // Apply scale with type-specific adjustment
      const baseScale = scales[i] * scaleMultiplier;
      instance.scale.set(
        baseScale,
        baseScale,
        baseScale
      );
      
      instanceGroup.add(instance);
    }
    
    console.log(`Adding ${instanceGroup.children.length} new instances to scene`);
    scene.add(instanceGroup);
    this.vegetationObjects.push(instanceGroup);
    
    // Store the new mesh with both keys for future reference
    this.instancedMeshes[vegetationType] = instanceGroup;
    if (meshKey && meshKey !== vegetationType) {
      this.instancedMeshes[meshKey] = instanceGroup;
    }
    
    console.log(`Successfully updated ${instanceGroup.children.length} instances of ${vegetationType}`);
  }

  /**
   * Create instanced mesh
   * @param {string} vegetationType - Type of vegetation
   * @param {number[]} positions - Flattened array of positions
   * @param {number[]} scales - Flattened array of scales
   * @param {number[]} rotations - Flattened array of rotations
   * @param {THREE.Scene} scene - Three.js scene
   * @returns {THREE.InstancedMesh} Instanced mesh
   */
  createInstancedMesh(vegetationType, positions, scales, rotations, scene) {
    const count = positions.length / 3;
    const geometry = this.createSimplifiedGeometry(vegetationType);
    const material = this.createMaterialForType(vegetationType);
    
    // Create instanced mesh
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    
    // Set the matrices for each instance
    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
      // Get position, scale, and rotation for this instance
      dummy.position.set(
        positions[i * 3],
        positions[i * 3 + 1],
        positions[i * 3 + 2]
      );
      
      dummy.scale.set(
        scales[i * 3],
        scales[i * 3 + 1],
        scales[i * 3 + 2]
      );
      
      dummy.rotation.set(
        rotations[i * 3],
        rotations[i * 3 + 1],
        rotations[i * 3 + 2]
      );
      
      // Update matrix and set it on the instanced mesh
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    
    // Set shadow properties based on vegetation type
    // Trees cast shadows, grass doesn't
    if (vegetationType.startsWith('TREE_')) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    } else if (vegetationType.startsWith('BUSH_')) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    } else if (vegetationType.startsWith('GRASS_')) {
      // Grass doesn't cast shadows for performance reasons
      mesh.castShadow = false;
      mesh.receiveShadow = true;
    }
    
    // Add the instanced mesh to the scene
    scene.add(mesh);
    
    // Store the instanced mesh for later reference
    this.vegetationObjects.push(mesh);
    this.instancedMeshes[vegetationType] = mesh;
    
    return mesh;
  }

  /**
   * Create instanced mesh from loaded GLTF model
   * @param {string} vegetationType - Type of vegetation
   * @param {number[]} positions - Flattened array of positions
   * @param {number[]} scales - Flattened array of scales
   * @param {number[]} rotations - Flattened array of rotations
   * @param {THREE.Scene} scene - Three.js scene
   */
  createInstancedMeshFromLoaded(vegetationType, positions, scales, rotations, scene) {
    const count = positions.length / 3;
    const loadedModel = this.loadedModels[vegetationType];
    
    console.log(`Creating ${count} instances of loaded ${vegetationType} model`);
    
    if (!loadedModel) {
      console.warn(`No loaded model for ${vegetationType}, using procedural fallback`);
      // Fall back to the regular instanced mesh creation
      const geometry = this.createSimplifiedGeometry(vegetationType);
      const material = this.createMaterialForType(vegetationType);
      const mesh = new THREE.InstancedMesh(geometry, material, count);
      
      const dummy = new THREE.Object3D();
      for (let i = 0; i < count; i++) {
        dummy.position.set(
          positions[i * 3],
          positions[i * 3 + 1],
          positions[i * 3 + 2]
        );
        dummy.scale.set(
          scales[i * 3],
          scales[i * 3 + 1],
          scales[i * 3 + 2]
        );
        dummy.rotation.set(
          rotations[i * 3],
          rotations[i * 3 + 1],
          rotations[i * 3 + 2]
        );
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
      }
      
      // If this is a tree, mark it for potential collision detection
      mesh.userData = {
        type: 'instanced-vegetation',
        vegetationType: vegetationType
      };
      
      // Set shadow properties based on vegetation type
      // Trees cast shadows, grass doesn't
      if (vegetationType.startsWith('TREE_')) {
        mesh.userData.isTree = true;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      } else if (vegetationType.startsWith('BUSH_')) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      } else if (vegetationType.startsWith('GRASS_')) {
        // Grass doesn't cast shadows for performance reasons
        mesh.castShadow = false;
        mesh.receiveShadow = true;
      }
      
      scene.add(mesh);
      this.vegetationObjects.push(mesh);
      this.instancedMeshes[vegetationType] = mesh;
      return mesh;
    }
    
    // For more complex models like trees or custom models,
    // we might need to create individual clones instead of instanced mesh
    // If the number of instances is small enough (to avoid performance issues)
    if (count < 30) {
      console.log(`Using individual clones for ${vegetationType} (count: ${count})`);
      
      const group = new THREE.Group();
      for (let i = 0; i < count; i++) {
        const instance = loadedModel.scene.clone();
        instance.position.set(
          positions[i * 3],
          positions[i * 3 + 1],
          positions[i * 3 + 2]
        );
        instance.scale.set(
          scales[i * 3],
          scales[i * 3 + 1],
          scales[i * 3 + 2]
        );
        instance.rotation.set(
          rotations[i * 3],
          rotations[i * 3 + 1],
          rotations[i * 3 + 2]
        );
        
        // Apply polygon offset to trees and bushes for better shadow rendering
        if (vegetationType.startsWith('TREE_') || vegetationType.startsWith('BUSH_')) {
          instance.traverse(child => {
            if (child.isMesh && child.material) {
              if (Array.isArray(child.material)) {
                child.material = child.material.map(m => {
                  const matClone = m.clone();
                  matClone.polygonOffset = true;
                  matClone.polygonOffsetFactor = -0.2;
                  matClone.polygonOffsetUnits = -1;
                  return matClone;
                });
              } else {
                child.material = child.material.clone();
                child.material.polygonOffset = true;
                child.material.polygonOffsetFactor = -0.2;
                child.material.polygonOffsetUnits = -1;
              }
            }
          });
        }
        
        // For trees, add a collision box
        if (vegetationType.startsWith('TREE_')) {
          instance.userData = {
            isObstacle: true,
            type: 'tree',
            vegetationType: vegetationType
          };
          
          // Add collision mesh for each tree instance
          const scale = scales[i * 3]; // Use x scale
          const trunkHeight = 2.0 * scale;
          const trunkRadius = 0.3 * scale;
          this.addCollisionMesh(
            instance,
            trunkRadius * 2,
            trunkHeight,
            trunkRadius * 2,
            new THREE.Vector3(0, trunkHeight / 2, 0)
          );
        }
        
        group.add(instance);
        this.vegetationObjects.push(instance);
      }
      
      // Tag the group
      group.userData = {
        type: 'vegetation-group',
        vegetationType: vegetationType
      };
      
      scene.add(group);
      this.vegetationObjects.push(group);
      return group;
    }
    
    // Otherwise, create a true instanced mesh from the loaded model
    // This is more complex and requires extracting the geometry from the loaded model
    // To simplify, we'll use a simplified geometry representation for instancing
    
    // Extract a representative geometry from the loaded model
    const geometry = this.createSimplifiedGeometryFromLoaded(loadedModel, vegetationType);
    const material = new THREE.MeshLambertMaterial({
      color: this.getColorForType(vegetationType)
    });
    
    // Apply polygon offset to tree and bush materials for better shadow rendering
    if (vegetationType.startsWith('TREE_') || vegetationType.startsWith('BUSH_')) {
      material.polygonOffset = true;
      material.polygonOffsetFactor = -0.2;
      material.polygonOffsetUnits = -1;
    }
    
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    
    const dummy = new THREE.Object3D();
    for (let i = 0; i < count; i++) {
      dummy.position.set(
        positions[i * 3],
        positions[i * 3 + 1],
        positions[i * 3 + 2]
      );
      dummy.scale.set(
        scales[i * 3],
        scales[i * 3 + 1],
        scales[i * 3 + 2]
      );
      dummy.rotation.set(
        rotations[i * 3],
        rotations[i * 3 + 1],
        rotations[i * 3 + 2]
      );
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    
    // If this is a tree, mark it for potential collision detection
    mesh.userData = {
      type: 'instanced-vegetation',
      vegetationType: vegetationType
    };
    
    if (vegetationType.startsWith('TREE_')) {
      mesh.userData.isTree = true;
    }
    
    scene.add(mesh);
    this.vegetationObjects.push(mesh);
    this.instancedMeshes[vegetationType] = mesh;
    return mesh;
  }

  /**
   * Create a simplified geometry for instanced meshes
   * @param {string} vegetationType - Type of vegetation
   * @returns {THREE.BufferGeometry} Simplified geometry
   */
  createSimplifiedGeometry(vegetationType) {
    if (vegetationType.startsWith('TREE_')) {
      // For trees, use a simple combination of cylinder and cone/sphere
      const geometry = new THREE.BufferGeometry();
      
      // Create merged geometry from trunk and crown
      const positions = [];
      const normals = [];
      
      // Add trunk (cylinder)
      const trunkGeometry = new THREE.CylinderGeometry(0.1, 0.15, 0.8, 8);
      trunkGeometry.translate(0, 0.4, 0);
      
      // Add crown (sphere for most, cone for pine)
      let crownGeometry;
      if (vegetationType === 'TREE_PINE') {
        crownGeometry = new THREE.ConeGeometry(0.6, 1.2, 8);
      } else {
        crownGeometry = new THREE.SphereGeometry(0.5, 8, 6);
      }
      crownGeometry.translate(0, 1.4, 0);
      
      // Extract positions and normals
      this.addGeometryAttributes(trunkGeometry, positions, normals);
      this.addGeometryAttributes(crownGeometry, positions, normals);
      
      // Set attributes
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      
      return geometry;
    } else if (vegetationType.startsWith('BUSH_')) {
      // For bushes, use a sphere
      return new THREE.SphereGeometry(0.5, 8, 6);
    } else if (vegetationType.startsWith('GRASS_')) {
      // For grass, use crossed planes
      return new THREE.PlaneGeometry(0.5, 1);
    }
  }

  /**
   * Add geometry attributes to arrays
   * @param {THREE.BufferGeometry} geometry - Geometry to extract attributes from
   * @param {number[]} positions - Positions array to add to
   * @param {number[]} normals - Normals array to add to
   */
  addGeometryAttributes(geometry, positions, normals) {
    const posAttr = geometry.attributes.position;
    const normalAttr = geometry.attributes.normal;
    
    for (let i = 0; i < posAttr.count; i++) {
      positions.push(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      normals.push(normalAttr.getX(i), normalAttr.getY(i), normalAttr.getZ(i));
    }
  }

  /**
   * Create material for a vegetation type
   * @param {string} vegetationType - Type of vegetation
   * @returns {THREE.Material} Material for the vegetation type
   */
  createMaterialForType(vegetationType) {
    let material;
    
    if (vegetationType.startsWith('TREE_')) {
      material = new THREE.MeshLambertMaterial({ 
        color: vegetationType === 'TREE_PINE' ? 0x1a6e32 : 0x2a8e42,
        vertexColors: true
      });
      
      // Apply polygon offset to fix shadow rendering issues
      material.polygonOffset = true;
      material.polygonOffsetFactor = -0.2;
      material.polygonOffsetUnits = -1;
      
    } else if (vegetationType.startsWith('BUSH_')) {
      material = new THREE.MeshLambertMaterial({ 
        color: vegetationType === 'BUSH_DESERT' ? 0x8e8e42 : 0x338833 
      });
      
      // Apply polygon offset to fix shadow rendering issues
      material.polygonOffset = true;
      material.polygonOffsetFactor = -0.2;
      material.polygonOffsetUnits = -1;
      
    } else if (vegetationType.startsWith('GRASS_')) {
      material = new THREE.MeshLambertMaterial({ 
        color: 0x559944,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: 0.5
      });
    }
    
    return material;
  }

  /**
   * Create individual vegetation object (used when instancing is disabled)
   * @param {string} vegetationType - Type of vegetation
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position
   * @param {number} scale - Scale factor
   * @param {number} rotation - Y-axis rotation
   * @param {THREE.Scene} scene - Three.js scene to add the vegetation to
   */
  createIndividualVegetation(vegetationType, x, y, z, scale, rotation, scene) {
    const model = this.models[vegetationType];
    if (!model) {
      console.error(`No model found for vegetation type: ${vegetationType}`);
      return;
    }
    
    // If we have a loaded model for this type, use that
    if (this.loadedModels[vegetationType]) {
      // Clone the model
      const instance = this.loadedModels[vegetationType].scene.clone();
      
      // Set position, rotation, and scale
      instance.position.set(x, y, z);
      instance.rotation.y = rotation;
      instance.scale.set(scale, scale, scale);
      
      // Set shadow properties based on vegetation type
      if (vegetationType.startsWith('TREE_')) {
        // Enable shadows for trees and apply polygon offset
        instance.traverse(object => {
          if (object.isMesh) {
            object.castShadow = true;
            object.receiveShadow = true;
            
            // Apply polygon offset to fix shadow rendering issues
            if (object.material) {
              // Ensure material is cloned to avoid affecting other instances
              if (!object.material._isCloned) {
                if (Array.isArray(object.material)) {
                  object.material = object.material.map(m => m.clone());
                  object.material.forEach(m => {
                    m._isCloned = true;
                    // Apply polygon offset
                    m.polygonOffset = true;
                    m.polygonOffsetFactor = -0.2;
                    m.polygonOffsetUnits = -1;
                  });
                } else {
                  object.material = object.material.clone();
                  object.material._isCloned = true;
                  // Apply polygon offset
                  object.material.polygonOffset = true;
                  object.material.polygonOffsetFactor = -0.2;
                  object.material.polygonOffsetUnits = -1;
                }
              }
            }
          }
        });
        
        // Mark trees as obstacles
        instance.userData = {
          isObstacle: true,
          type: 'tree',
          vegetationType: vegetationType
        };
        
        // Add a collision mesh if it's a tree and doesn't already have one
        const trunkHeight = 2.0 * scale; // Approximate trunk height
        const trunkRadius = 0.3 * scale; // Approximate trunk radius
        this.addCollisionMesh(
          instance,
          trunkRadius * 2,
          trunkHeight,
          trunkRadius * 2,
          new THREE.Vector3(0, trunkHeight / 2, 0)
        );
        
        // Ensure all meshes in the tree have bounding boxes
        instance.traverse(object => {
          if (object.isMesh && !object.geometry.boundingBox) {
            object.geometry.computeBoundingBox();
          }
        });
      } else if (vegetationType.startsWith('BUSH_')) {
        // Enable shadows for bushes and apply polygon offset
        instance.traverse(object => {
          if (object.isMesh) {
            object.castShadow = true;
            object.receiveShadow = true;
            
            // Apply polygon offset to fix shadow rendering issues
            if (object.material) {
              // Ensure material is cloned
              if (!object.material._isCloned) {
                if (Array.isArray(object.material)) {
                  object.material = object.material.map(m => m.clone());
                  object.material.forEach(m => {
                    m._isCloned = true;
                    m.polygonOffset = true;
                    m.polygonOffsetFactor = -0.2;
                    m.polygonOffsetUnits = -1;
                  });
                } else {
                  object.material = object.material.clone();
                  object.material._isCloned = true;
                  object.material.polygonOffset = true;
                  object.material.polygonOffsetFactor = -0.2;
                  object.material.polygonOffsetUnits = -1;
                }
              }
            }
          }
        });
        
        // Mark bushes as obstacles
        instance.userData = {
          isObstacle: true,
          type: 'bush',
          vegetationType: vegetationType
        };
        
        // Get dimensions for this type of bush
        const vegType = VegetationSystem.VEGETATION_TYPES[vegetationType];
        if (!vegType || !vegType.dimensions) {
          console.error(`No dimensions defined for bush type: ${vegetationType}`);
          return;
        }
        
        // Apply bush scale to the base dimensions
        const width = vegType.dimensions.width * scale;
        const height = vegType.dimensions.height * scale;
        const depth = vegType.dimensions.depth * scale;
        
        // Add a collision mesh for the bush using predefined dimensions
        this.addCollisionMesh(
          instance,
          width,
          height,
          depth,
          new THREE.Vector3(0, height / 2, 0)
        );
        
        // Ensure all meshes in the bush have bounding boxes
        instance.traverse(object => {
          if (object.isMesh && !object.geometry.boundingBox) {
            object.geometry.computeBoundingBox();
          }
        });
      } else if (vegetationType.startsWith('GRASS_')) {
        // Disable shadow casting for grass
        instance.traverse(object => {
          if (object.isMesh) {
            object.castShadow = false;
            object.receiveShadow = true;
          }
        });
      }
      
      scene.add(instance);
      this.vegetationObjects.push(instance);
      return;
    }
    
    // Clone the model for procedurally generated vegetation
    let vegetation;
    if (model.geometry instanceof THREE.Group) {
      vegetation = model.geometry.clone();
    } else {
      vegetation = model.geometry.clone();
    }
    
    // Set position, rotation, and scale
    vegetation.position.set(x, y, z);
    vegetation.rotation.y = rotation;
    vegetation.scale.set(scale, scale, scale);
    
    // Set shadow properties based on vegetation type
    if (vegetationType.startsWith('TREE_')) {
      vegetation.traverse(object => {
        if (object.isMesh) {
          object.castShadow = true;
          object.receiveShadow = true;
          
          // Apply polygon offset to fix shadow rendering issues
          if (object.material) {
            // Ensure material is cloned
            if (!object.material._isCloned) {
              if (Array.isArray(object.material)) {
                object.material = object.material.map(m => m.clone());
                object.material.forEach(m => {
                  m._isCloned = true;
                  m.polygonOffset = true;
                  m.polygonOffsetFactor = -0.2;
                  m.polygonOffsetUnits = -1;
                });
              } else {
                object.material = object.material.clone();
                object.material._isCloned = true;
                object.material.polygonOffset = true;
                object.material.polygonOffsetFactor = -0.2;
                object.material.polygonOffsetUnits = -1;
              }
            }
          }
        }
      });
      
      // Mark trees as obstacles
      vegetation.userData = {
        isObstacle: true,
        type: 'tree',
        vegetationType: vegetationType
      };
      
      // Ensure all meshes in the tree have bounding boxes
      vegetation.traverse(object => {
        if (object.isMesh && !object.geometry.boundingBox) {
          object.geometry.computeBoundingBox();
        }
      });
    } else if (vegetationType.startsWith('BUSH_')) {
      vegetation.traverse(object => {
        if (object.isMesh) {
          object.castShadow = true;
          object.receiveShadow = true;
          
          // Apply polygon offset to bushes too
          if (object.material) {
            // Ensure material is cloned
            if (!object.material._isCloned) {
              if (Array.isArray(object.material)) {
                object.material = object.material.map(m => m.clone());
                object.material.forEach(m => {
                  m._isCloned = true;
                  m.polygonOffset = true;
                  m.polygonOffsetFactor = -0.2;
                  m.polygonOffsetUnits = -1;
                });
              } else {
                object.material = object.material.clone();
                object.material._isCloned = true;
                object.material.polygonOffset = true;
                object.material.polygonOffsetFactor = -0.2;
                object.material.polygonOffsetUnits = -1;
              }
            }
          }
        }
      });
    } else if (vegetationType.startsWith('GRASS_')) {
      vegetation.traverse(object => {
        if (object.isMesh) {
          object.castShadow = false;
          object.receiveShadow = true;
        }
      });
    }
    
    // Add to scene
    scene.add(vegetation);
    this.vegetationObjects.push(vegetation);
  }

  /**
   * Seeded random number generator
   * @param {string|number} seed - Seed value
   * @returns {Function} Function that returns a random number between 0 and 1
   */
  seededRandom(seed) {
    const seedValue = typeof seed === 'string' ? 
      seed.split('').reduce((a, b) => (a * 21 + b.charCodeAt(0)) % 2147483647, 0) : 
      seed % 2147483647;
    
    let value = seedValue;
    return function() {
      value = (value * 16807) % 2147483647;
      return (value - 1) / 2147483646;
    };
  }

  /**
   * Create a visualization of the vegetation density map for debugging
   * @param {number[][]} densityMap - 2D array of density values
   * @returns {string} Data URL of the density map visualization
   */
  visualizeDensityMap(densityMap) {
    const width = densityMap[0].length;
    const height = densityMap.length;
    
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const density = densityMap[y][x];
        const index = (y * width + x) * 4;
        
        // Use a green gradient for density
        imageData.data[index] = 0;
        imageData.data[index + 1] = Math.floor(density * 255);
        imageData.data[index + 2] = 0;
        imageData.data[index + 3] = 255;
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }

  /**
   * Create a simple grass shader material based on the example
   * @returns {THREE.ShaderMaterial} The shader material
   */
  createSimpleGrassMaterial() {
    const material = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: null },
        alphaMap: { value: null },
        time: { value: 0 },
        tipColor: { value: new THREE.Color(0.0, 0.6, 0.0) },
        bottomColor: { value: new THREE.Color(0.0, 0.1, 0.0) },
        bladeHeight: { value: 0.1 }
      },
      vertexShader: `   
      precision mediump float;
      attribute vec3 offset;
      attribute vec4 orientation;
      attribute float halfRootAngleSin;
      attribute float halfRootAngleCos;
      attribute float stretch;
      uniform float time;
      uniform float bladeHeight;
      varying vec2 vUv;
      varying float frc;
      
      //WEBGL-NOISE FROM https://github.com/stegu/webgl-noise
      //Description : Array and textureless GLSL 2D simplex noise function. Author : Ian McEwan, Ashima Arts. Maintainer : stegu Lastmod : 20110822 (ijm) License : Copyright (C) 2011 Ashima Arts. All rights reserved. Distributed under the MIT License. See LICENSE file. https://github.com/ashima/webgl-noise https://github.com/stegu/webgl-noise      
      vec3 mod289(vec3 x) {return x - floor(x * (1.0 / 289.0)) * 289.0;} vec2 mod289(vec2 x) {return x - floor(x * (1.0 / 289.0)) * 289.0;} vec3 permute(vec3 x) {return mod289(((x*34.0)+1.0)*x);} float snoise(vec2 v){const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439); vec2 i  = floor(v + dot(v, C.yy) ); vec2 x0 = v -   i + dot(i, C.xx); vec2 i1; i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0); vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1; i = mod289(i); vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 )); vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0); m = m*m ; m = m*m ; vec3 x = 2.0 * fract(p * C.www) - 1.0; vec3 h = abs(x) - 0.5; vec3 ox = floor(x + 0.5); vec3 a0 = x - ox; m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h ); vec3 g; g.x  = a0.x  * x0.x  + h.x  * x0.y; g.yz = a0.yz * x12.xz + h.yz * x12.yw; return 130.0 * dot(m, g);}
      //END NOISE
      
      //https://www.geeks3d.com/20141201/how-to-rotate-a-vertex-by-a-quaternion-in-glsl/
      vec3 rotateVectorByQuaternion( vec3 v, vec4 q){
        return 2.0 * cross(q.xyz, v * q.w + cross(q.xyz, v)) + v;
      }
      
      //https://en.wikipedia.org/wiki/Slerp
      vec4 slerp(vec4 v0, vec4 v1, float t) {
        // Only unit quaternions are valid rotations.
        // Normalize to avoid undefined behavior.
        normalize(v0);
        normalize(v1);
      
        // Compute the cosine of the angle between the two vectors.
        float dot_ = dot(v0, v1);
      
        // If the dot product is negative, slerp won't take
        // the shorter path. Note that v1 and -v1 are equivalent when
        // the negation is applied to all four components. Fix by 
        // reversing one quaternion.
        if (dot_ < 0.0) {
          v1 = -v1;
          dot_ = -dot_;
        }  
      
        const float DOT_THRESHOLD = 0.9995;
        if (dot_ > DOT_THRESHOLD) {
          // If the inputs are too close for comfort, linearly interpolate
          // and normalize the result.
          vec4 result = t*(v1 - v0) + v0;
          normalize(result);
          return result;
        }
      
        // Since dot is in range [0, DOT_THRESHOLD], acos is safe
        float theta_0 = acos(dot_);       // theta_0 = angle between input vectors
        float theta = theta_0*t;          // theta = angle between v0 and result
        float sin_theta = sin(theta);     // compute this value only once
        float sin_theta_0 = sin(theta_0); // compute this value only once
        float s0 = cos(theta) - dot_ * sin_theta / sin_theta_0;  // == sin(theta_0 - theta) / sin(theta_0)
        float s1 = sin_theta / sin_theta_0;
        return (s0 * v0) + (s1 * v1);
      }
      
      void main() {
        //Relative position of vertex along the mesh Y direction
        frc = position.y/float(bladeHeight);
        //Get wind data from simplex noise 
        float noise = 1.0-(snoise(vec2((time-offset.x/50.0), (time-offset.z/50.0)))); 
        //Define the direction of an unbent blade of grass rotated around the Y axis
        vec4 direction = vec4(0.0, halfRootAngleSin, 0.0, halfRootAngleCos);
        //Interpolate between the unbent direction and the direction of growth calculated on the CPU. 
        //Using the relative location of the vertex along the Y axis as the weight, we get a smooth bend
        direction = slerp(direction, orientation, frc);
        vec3 vPosition = vec3(position.x, position.y + position.y * stretch, position.z);
        vPosition = rotateVectorByQuaternion(vPosition, direction);
      
       //Apply wind
       float halfAngle = noise * 0.15;
        vPosition = rotateVectorByQuaternion(vPosition, normalize(vec4(sin(halfAngle), 0.0, -sin(halfAngle), cos(halfAngle))));
        //UV for texture
        vUv = uv;
        //Calculate final position of the vertex from the world offset and the above shenanigans 
        gl_Position = projectionMatrix * modelViewMatrix * vec4(offset + vPosition, 1.0 );
      }`,
      fragmentShader: `
      precision mediump float;
      uniform sampler2D map;
      uniform sampler2D alphaMap;
      uniform vec3 tipColor;
      uniform vec3 bottomColor;
      varying vec2 vUv;
      varying float frc;
      
      void main() {
        //Get transparency information from alpha map
        float alpha = texture2D(alphaMap, vUv).r;
        //If transparent, don't draw
        if(alpha < 0.15) discard;
        //Get colour data from texture
        vec4 col = vec4(texture2D(map, vUv));
        //Add more green towards root
        col = mix(vec4(tipColor, 1.0), col, frc);
        //Add a shadow towards root
        col = mix(vec4(bottomColor, 1.0), col, frc);
        gl_FragColor = col;

        #include <tonemapping_fragment>
	      #include <encodings_fragment>
      }`,
      side: THREE.DoubleSide
    });
    
    // Enable shadow map support
    material.customProgramCacheKey = () => Math.random();
    material.onBeforeCompile = (shader) => {
      // Add shadow map uniforms
      shader.uniforms = THREE.UniformsUtils.merge([
        shader.uniforms,
        THREE.UniformsLib.lights
      ]);
      
      // Enable lights in the material
      material.lights = true;
      
      // Add shadow map defines
      shader.defines = shader.defines || {};
      shader.defines.USE_SHADOWMAP = '';
      shader.defines.SHADOWMAP_TYPE_PCF_SOFT = '';
      
      // Add common lighting parameters to vertex shader
      shader.vertexShader = shader.vertexShader.replace(
        'void main() {',
        `varying vec3 vViewPosition;
        #include <common>
        #include <shadowmap_pars_vertex>
        
        void main() {`
      );
      
      // Add shadowmap computation to vertex shader
      shader.vertexShader = shader.vertexShader.replace(
        'gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);',
        `vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
        vViewPosition = -mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
        
        // Shadow mapping vertex operations
        #include <shadowmap_vertex>`
      );
      
      // Add shadowmap to fragment shader
      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        `varying vec3 vViewPosition;
        #include <common>
        #include <packing>
        #include <bsdfs>
        #include <lights_pars_begin>
        #include <shadowmap_pars_fragment>
        #include <shadowmask_pars_fragment>
        
        void main() {`
      );
      
      // Add shadow effect to final color
      shader.fragmentShader = shader.fragmentShader.replace(
        'gl_FragColor = col;',
        `// Calculate shadow effect
        float shadowEffect = receiveShadow ? (1.0 - getShadowMask()) * 0.3 : 0.0;
        
        // Apply shadows to final color
        gl_FragColor = col;
        gl_FragColor.rgb *= (1.0 - shadowEffect);`
      );
      
      // Add receiveShadow uniform if it doesn't exist
      if (shader.uniforms.receiveShadow === undefined) {
        shader.uniforms.receiveShadow = { value: true };
      }
    };
    
    // Enable lights in the material
    material.lights = true;
    
    return material;
  }

  /**
   * Place shader-based grass
   * @param {string} vegetationType - Type of vegetation
   * @param {number[][]} heightMap - 2D array of height values
   * @param {Object[][]} biomeMap - 2D array of biome objects
   * @param {number[][]} densityMap - 2D array of density values
   * @param {THREE.Scene} scene - Three.js scene
   */
  placeShaderGrass(vegetationType, heightMap, biomeMap, densityMap, scene) {
    console.log(`Creating optimized grass for ${vegetationType}`);
    
    const vegType = VegetationSystem.VEGETATION_TYPES[vegetationType];
    
    try {
      // Clear the previous emergency code and implement proper shader-based grass
      const width = heightMap[0].length;
      const height = heightMap.length;
      const worldSize = this.options.worldSize;
      
      // Adjust grass count based on quality setting
      let instances;
      switch(this.options.grassQuality) {
        case 'low': 
          instances = 100000; 
          break;
        case 'medium': 
          instances = 200000; 
          break;
        case 'high': 
          instances = 300000; 
          break;
        case 'ultra': 
          instances = 400000; 
          break;
        default: 
          instances = 200000;
      }

      // Store the base instance count for adaptive grass adjustment
      this.fpsMonitoring.baseInstanceCount = instances;
      
      // If adaptive grass is enabled, start with a conservative instance count
      if (this.options.adaptiveGrass) {
        this.fpsMonitoring.currentInstanceCount = instances;
        console.log(`Adaptive grass enabled, starting with ${instances} instances and will adjust based on performance`);
      }
      
      console.log(`Generating ${instances} grass blades with optimized performance`);
      
      // Get texture and alpha map
      const textureLoader = new THREE.TextureLoader();
      let bladeDiffuseTexture, bladeAlphaTexture;
      
      // Load textures - note: in a production environment, these should be preloaded
      try {
        bladeDiffuseTexture = textureLoader.load('/models/vegetation/blade_diffuse.jpg');
        bladeAlphaTexture = textureLoader.load('/models/vegetation/blade_alpha.jpg');
        console.log('Loaded grass textures successfully');
      } catch (error) {
        console.error('Failed to load grass textures:', error);
        // Create fallback textures
        bladeDiffuseTexture = new THREE.DataTexture(new Uint8Array([0, 255, 0, 255]), 1, 1, THREE.RGBAFormat);
        bladeAlphaTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat);
      }
      
      // Generate optimized attribute data - passing biomeMap to restrict grass to green areas
      const attributeData = this.getGrassAttributeData(instances, worldSize, heightMap, biomeMap);
      console.log(`Generated attribute data for ${attributeData.offsets.length / 3} grass blades`);
      
      // Create blade geometry (similar to the React example)
      // FIX: Use PlaneGeometry instead of PlaneBufferGeometry
      const bladeWidth = 0.02;
      const bladeHeight = 0.2;
      const joints = 5;
      
      // Changed translation to ensure blade grows from terrain surface
      // Instead of centering at half height, we anchor at the bottom (0)
      const bladeGeometry = new THREE.PlaneGeometry(bladeWidth, bladeHeight, 1, joints)
        .translate(0, bladeHeight / 2, 0);
      
      // Create instanced buffer geometry
      const instancedGeometry = new THREE.InstancedBufferGeometry();
      instancedGeometry.index = bladeGeometry.index;
      instancedGeometry.attributes.position = bladeGeometry.attributes.position;
      instancedGeometry.attributes.uv = bladeGeometry.attributes.uv;
      
      // Add instance attributes
      instancedGeometry.setAttribute('offset', 
        new THREE.InstancedBufferAttribute(new Float32Array(attributeData.offsets), 3));
      instancedGeometry.setAttribute('orientation', 
        new THREE.InstancedBufferAttribute(new Float32Array(attributeData.orientations), 4));
      instancedGeometry.setAttribute('stretch', 
        new THREE.InstancedBufferAttribute(new Float32Array(attributeData.stretches), 1));
      instancedGeometry.setAttribute('halfRootAngleSin', 
        new THREE.InstancedBufferAttribute(new Float32Array(attributeData.halfRootAngleSin), 1));
      instancedGeometry.setAttribute('halfRootAngleCos', 
        new THREE.InstancedBufferAttribute(new Float32Array(attributeData.halfRootAngleCos), 1));
      
      // Store full attribute data for adaptive density adjustments
      this.grassAttributeData = attributeData;
      
      // Create optimized grass material with simplified shader (no wind)
      const grassMaterial = new THREE.ShaderMaterial({
          uniforms: {
            time: { value: 0 },
            bladeHeight: { value: bladeHeight },
            map: { value: bladeDiffuseTexture },
            alphaMap: { value: bladeAlphaTexture },
            tipColor: { value: new THREE.Color(0.0, 0.6, 0.0).convertSRGBToLinear() },
            bottomColor: { value: new THREE.Color(0.0, 0.1, 0.0).convertSRGBToLinear() },
            playerPosition: { value: new THREE.Vector3(0, 0, 0) },
            playerStrength: { value: 0.0 }
          },
          vertexShader: `   
          precision mediump float;
          attribute vec3 offset;
          attribute vec4 orientation;
          attribute float halfRootAngleSin;
          attribute float halfRootAngleCos;
          attribute float stretch;
          uniform float time;
          uniform float bladeHeight;
          varying vec2 vUv;
          varying float frc;
          uniform vec3 playerPosition;
          uniform float playerStrength;
          
          //https://www.geeks3d.com/20141201/how-to-rotate-a-vertex-by-a-quaternion-in-glsl/
          vec3 rotateVectorByQuaternion(vec3 v, vec4 q){
            return 2.0 * cross(q.xyz, v * q.w + cross(q.xyz, v)) + v;
          }
          
          //https://en.wikipedia.org/wiki/Slerp
          vec4 slerp(vec4 v0, vec4 v1, float t) {
            // Only unit quaternions are valid rotations.
            // Normalize to avoid undefined behavior.
            normalize(v0);
            normalize(v1);
          
            // Compute the cosine of the angle between the two vectors.
            float dot_ = dot(v0, v1);
          
            // If the dot product is negative, slerp won't take
            // the shorter path. Note that v1 and -v1 are equivalent when
            // the negation is applied to all four components. Fix by 
            // reversing one quaternion.
            if (dot_ < 0.0) {
              v1 = -v1;
              dot_ = -dot_;
            }  
          
            const float DOT_THRESHOLD = 0.9995;
            if (dot_ > DOT_THRESHOLD) {
              // If the inputs are too close for comfort, linearly interpolate
              // and normalize the result.
              vec4 result = t*(v1 - v0) + v0;
              normalize(result);
              return result;
            }
          
            // Since dot is in range [0, DOT_THRESHOLD], acos is safe
            float theta_0 = acos(dot_);       // theta_0 = angle between input vectors
            float theta = theta_0*t;          // theta = angle between v0 and result
            float sin_theta = sin(theta);     // compute this value only once
            float sin_theta_0 = sin(theta_0); // compute this value only once
            float s0 = cos(theta) - dot_ * sin_theta / sin_theta_0;  // == sin(theta_0 - theta) / sin(theta_0)
            float s1 = sin_theta / sin_theta_0;
            return (s0 * v0) + (s1 * v1);
          }
          
          void main() {
            //Relative position of vertex along the mesh Y direction
            frc = position.y/float(bladeHeight);
            
            //Define the direction of an unbent blade of grass rotated around the Y axis
            vec4 direction = vec4(0.0, halfRootAngleSin, 0.0, halfRootAngleCos);
            //Interpolate between the unbent direction and the direction of growth calculated on the CPU. 
            //Using the relative location of the vertex along the Y axis as the weight, we get a smooth bend
            direction = slerp(direction, orientation, frc);
            vec3 vPosition = vec3(position.x, position.y + position.y * stretch, position.z);
            vPosition = rotateVectorByQuaternion(vPosition, direction);
          
            // Player interaction (replacing wind)
            float playerDist = distance(playerPosition, offset);
            if (playerDist < 3.0 && playerStrength > 0.0) {
              vec3 toPlayer = normalize(offset - playerPosition);
              float playerFactor = (1.0 - playerDist / 3.0) * playerStrength * frc * frc;
              vPosition.x += toPlayer.x * playerFactor;
              vPosition.z += toPlayer.z * playerFactor;
            }
            
            //UV for texture
            vUv = uv;
            //Calculate final position of the vertex from the world offset and the above transformations
            gl_Position = projectionMatrix * modelViewMatrix * vec4(offset + vPosition, 1.0);
          }
        `,
          fragmentShader: `
          precision mediump float;
          uniform sampler2D map;
          uniform sampler2D alphaMap;
          uniform vec3 tipColor;
          uniform vec3 bottomColor;
          varying vec2 vUv;
          varying float frc;
          
          void main() {
            //Get transparency information from alpha map
            float alpha = texture2D(alphaMap, vUv).r;
            //If transparent, don't draw
            if(alpha < 0.15) discard;
            //Get colour data from texture
            vec4 col = vec4(texture2D(map, vUv));
            //Add more green towards root
            col = mix(vec4(tipColor, 1.0), col, frc);
            //Add a shadow towards root
            col = mix(vec4(bottomColor, 1.0), col, frc);
            gl_FragColor = col;
          }
        `,
        side: THREE.DoubleSide,
        transparent: true
      });
      
      // Enable shadow map support for the shader grass
      grassMaterial.customProgramCacheKey = () => Math.random();
      grassMaterial.onBeforeCompile = (shader) => {
        // Add shadow map uniforms
        shader.uniforms = THREE.UniformsUtils.merge([
          shader.uniforms,
          THREE.UniformsLib.lights
        ]);
        
        // Enable lights in the material
        grassMaterial.lights = true;
        
        // Add shadow map defines
        shader.defines = shader.defines || {};
        shader.defines.USE_SHADOWMAP = '';
        shader.defines.SHADOWMAP_TYPE_PCF_SOFT = '';
        
        // Add common lighting parameters to vertex shader
        shader.vertexShader = shader.vertexShader.replace(
          'void main() {',
          `varying vec3 vViewPosition;
          #include <common>
          #include <shadowmap_pars_vertex>
          
          void main() {`
        );
        
        // Add shadowmap computation to vertex shader
        shader.vertexShader = shader.vertexShader.replace(
          'gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);',
          `vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
          vViewPosition = -mvPosition.xyz;
          gl_Position = projectionMatrix * mvPosition;
          
          // Shadow mapping vertex operations
          #include <shadowmap_vertex>`
        );
        
        // Add shadowmap to fragment shader
        shader.fragmentShader = shader.fragmentShader.replace(
          'void main() {',
          `varying vec3 vViewPosition;
          #include <common>
          #include <packing>
          #include <bsdfs>
          #include <lights_pars_begin>
          #include <shadowmap_pars_fragment>
          #include <shadowmask_pars_fragment>
          
          void main() {`
        );
        
        // Add shadow effect to final color
        shader.fragmentShader = shader.fragmentShader.replace(
          'gl_FragColor = col;',
          `// Calculate shadow effect
          float shadowEffect = receiveShadow ? (1.0 - getShadowMask()) * 0.3 : 0.0;
          
          // Apply shadows to final color
          gl_FragColor = col;
          gl_FragColor.rgb *= (1.0 - shadowEffect);`
        );
        
        // Add receiveShadow uniform if it doesn't exist
        if (shader.uniforms.receiveShadow === undefined) {
          shader.uniforms.receiveShadow = { value: true };
        }
      };
      
      // Enable lights in the material
      grassMaterial.lights = true;
      
      // Create the mesh
      const grassMesh = new THREE.Mesh(instancedGeometry, grassMaterial);
      grassMesh.name = `${vegetationType}_shader_grass`;
      
      // FIXED: Disable frustum culling to prevent disappearing when looking directly down
      grassMesh.frustumCulled = false;
      
      // Make grass both cast and receive shadows
      grassMesh.castShadow = false; // Disable shadow casting for performance
      grassMesh.receiveShadow = true;
      
      // Add the grass mesh to the scene
      scene.add(grassMesh);
      this.vegetationObjects.push(grassMesh);
      
      // Store in grass shader systems for updates
      this.grassShaderSystems.push({
        mesh: grassMesh,
        material: grassMaterial,
        isAdvanced: true,
        type: vegetationType,
        // Add properties for adaptive density
        geometry: instancedGeometry,
        originalInstanceCount: instances,
        currentInstanceCount: instances,
      });
      
      console.log(`Successfully created optimized shader grass with ${instances} blades`);
      
      return;
    } catch (error) {
      console.error(`DEBUG: Error creating advanced grass - falling back:`, error);
      console.log(`DEBUG: Error stack:`, error.stack);
      console.log('Falling back to cross-plane grass');
    
      // Continue with fallback implementation
      // ... fallback code ...
    }
  }

  // Optimized method to generate attribute data for grass without wind
  getGrassAttributeData(instances, width, heightMap, biomeMap) {
    const offsets = [];
    const orientations = [];
    const stretches = [];
    const halfRootAngleSin = [];
    const halfRootAngleCos = [];

    // Define strictly which biomes should have grass
    // Be more restrictive with the biome list
    const greenBiomes = ['grass', 'grassland', 'plains', 'forest']; 
    
    // The rest of the height position function remains the same
    const getYPosition = (x, z) => {
      // Find the closest position in the height map
      const mapSize = heightMap.length;
      const worldToMap = mapSize / width;
      
      // Convert world coordinates to map coordinates
      const mapX = Math.floor((x + width/2) * worldToMap);
      const mapZ = Math.floor((z + width/2) * worldToMap);
      
      // Bounds checking
      if (mapX >= 0 && mapX < mapSize && mapZ >= 0 && mapZ < mapSize) {
        return heightMap[mapZ][mapX] * 10; // Scale height to match your terrain
      }
      return 0;
    };

    // STRICTER BIOME VALIDATION: More carefully validate biomes
    const isValidGrassBiome = (x, z) => {
      if (!biomeMap) return false; // If no biome map, don't allow grass by default
      
      const mapSize = biomeMap.length;
      const worldToMap = mapSize / width;
      
      // Convert world coordinates to map coordinates
      const mapX = Math.floor((x + width/2) * worldToMap);
      const mapZ = Math.floor((z + width/2) * worldToMap);
      
      // Bounds checking
      if (mapX >= 0 && mapX < mapSize && mapZ >= 0 && mapZ < mapSize) {
        const biome = biomeMap[mapZ][mapX];
        if (!biome || !biome.id) return false;
        
        // STRICT MATCHING: Only allow grass in specific green biomes
        if (greenBiomes.some(b => biome.id.includes(b))) return true;
        
        // EXPLICIT EXCLUSIONS: Definitely exclude these biomes
        const excludeBiomes = ['desert', 'water', 'ocean', 'river', 'lake', 'mountain', 'snow', 'ice', 'rock', 'beach', 'sand'];
        if (excludeBiomes.some(b => biome.id.includes(b))) return false;
        
        // For other biomes, only allow if they have explicit grass properties
        return biome.grassDensity !== undefined && biome.grassDensity > 0.5;
      }
      return false;
    };

    // IMPROVED GRID APPROACH: More efficient placement algorithm
    // Use adaptive grid size based on instance count for better performance
    const gridSize = Math.min(64, Math.ceil(Math.sqrt(instances / 10)));
    const gridCellWidth = width / gridSize;
    const gridCellDepth = width / gridSize;
    
    // First scan to count valid cells for better distribution
    let validCells = [];
    
    // More efficient scanning - just check center points first
    for (let gz = 0; gz < gridSize; gz++) {
      for (let gx = 0; gx < gridSize; gx++) {
        const cellCenterX = ((gx / gridSize) - 0.5) * width + (gridCellWidth * 0.5);
        const cellCenterZ = ((gz / gridSize) - 0.5) * width + (gridCellDepth * 0.5);
        
        if (isValidGrassBiome(cellCenterX, cellCenterZ)) {
          validCells.push({x: gx, z: gz});
        }
      }
    }
    
    console.log(`Found ${validCells.length} valid grass cells out of ${gridSize*gridSize} total cells`);
    
    // Calculate grass per cell based on valid cells
    const grassPerCell = Math.ceil(instances / Math.max(1, validCells.length));
    
    // Now place grass only in valid cells
    let validPositions = [];
    
    // Only process the valid cells we found
    for (let i = 0; i < validCells.length; i++) {
      const cell = validCells[i];
      
      // Calculate bounding box for this cell
      const cellMinX = ((cell.x / gridSize) - 0.5) * width;
      const cellMaxX = (((cell.x + 1) / gridSize) - 0.5) * width;
      const cellMinZ = ((cell.z / gridSize) - 0.5) * width;
      const cellMaxZ = (((cell.z + 1) / gridSize) - 0.5) * width;
      
      // Calculate how many grass instances to place in this cell
      const targetCount = Math.min(grassPerCell, instances - validPositions.length);
      if (targetCount <= 0) break;
      
      // Efficiently place grass using batch operations
      for (let j = 0; j < targetCount; j++) {
        // Generate random position within cell
        const offsetX = cellMinX + Math.random() * (cellMaxX - cellMinX);
        const offsetZ = cellMinZ + Math.random() * (cellMaxZ - cellMinZ);
        
        // We know the cell is valid, so we can skip the isValidGrassBiome check
        // However, we might still want to check the edges if precision is important
        // if (isValidGrassBiome(offsetX, offsetZ)) {
        const offsetY = getYPosition(offsetX, offsetZ);
        validPositions.push({x: offsetX, y: offsetY, z: offsetZ});
        // }
        
        if (validPositions.length >= instances) break;
      }
      
      if (validPositions.length >= instances) break;
    }
    
    console.log(`Placed ${validPositions.length} grass blades in valid biomes using optimized algorithm`);
    
    const actualInstances = validPositions.length;
    
    let quaternion_0 = new THREE.Vector4();
    let quaternion_1 = new THREE.Vector4();

    // The min and max angle for the growth direction (in radians)
    const min = -0.25;
    const max = 0.25;

    // Process the valid positions - use typed arrays for better performance
    for (let i = 0; i < actualInstances; i++) {
      const position = validPositions[i];
      
      // Add position to offsets with a small y-offset to prevent z-fighting
      offsets.push(position.x, position.y - 0.02, position.z);

      // Define random growth directions
      // Rotate around Y
      let angle = Math.PI - Math.random() * (2 * Math.PI);
      halfRootAngleSin.push(Math.sin(0.5 * angle));
      halfRootAngleCos.push(Math.cos(0.5 * angle));

      // Create quaternion for Y-axis rotation
      let RotationAxis = new THREE.Vector3(0, 1, 0);
      let x = RotationAxis.x * Math.sin(angle / 2.0);
      let y = RotationAxis.y * Math.sin(angle / 2.0);
      let z = RotationAxis.z * Math.sin(angle / 2.0);
      let w = Math.cos(angle / 2.0);
      quaternion_0.set(x, y, z, w).normalize();

      // Rotate around X
      angle = Math.random() * (max - min) + min;
      RotationAxis = new THREE.Vector3(1, 0, 0);
      x = RotationAxis.x * Math.sin(angle / 2.0);
      y = RotationAxis.y * Math.sin(angle / 2.0);
      z = RotationAxis.z * Math.sin(angle / 2.0);
      w = Math.cos(angle / 2.0);
      quaternion_1.set(x, y, z, w).normalize();

      // Combine rotations to a single quaternion
      quaternion_0 = this.multiplyQuaternions(quaternion_0, quaternion_1);

      // Rotate around Z
      angle = Math.random() * (max - min) + min;
      RotationAxis = new THREE.Vector3(0, 0, 1);
      x = RotationAxis.x * Math.sin(angle / 2.0);
      y = RotationAxis.y * Math.sin(angle / 2.0);
      z = RotationAxis.z * Math.sin(angle / 2.0);
      w = Math.cos(angle / 2.0);
      quaternion_1.set(x, y, z, w).normalize();

      // Combine rotations to a single quaternion
      quaternion_0 = this.multiplyQuaternions(quaternion_0, quaternion_1);

      orientations.push(quaternion_0.x, quaternion_0.y, quaternion_0.z, quaternion_0.w);

      // VARIATION: More natural height distribution
      const heightRandom = Math.random();
      if (heightRandom < 0.05) {
        // 5% very tall grass
        stretches.push(Math.random() * 2.5 + 1.0); // 1.0 to 3.5
      } else if (heightRandom < 0.15) {
        // 10% tall grass
        stretches.push(Math.random() * 1.5 + 0.8); // 0.8 to 2.3
      } else if (heightRandom < 0.40) {
        // 25% medium grass
        stretches.push(Math.random() * 0.8 + 0.5); // 0.5 to 1.3
    } else {
        // 60% shorter grass
        stretches.push(Math.random() * 0.4 + 0.2); // 0.2 to 0.6
      }
    }

    return {
      offsets,
      orientations,
      stretches,
      halfRootAngleCos,
      halfRootAngleSin,
    };
  }

  // Add quaternion multiplication helper
  multiplyQuaternions(q1, q2) {
    const x = q1.x * q2.w + q1.y * q2.z - q1.z * q2.y + q1.w * q2.x;
    const y = -q1.x * q2.z + q1.y * q2.w + q1.z * q2.x + q1.w * q2.y;
    const z = q1.x * q2.y - q1.y * q2.x + q1.z * q2.w + q1.w * q2.z;
    const w = -q1.x * q2.x - q1.y * q2.y - q1.z * q2.z + q1.w * q2.w;
    return new THREE.Vector4(x, y, z, w);
  }

  /**
   * Get ultra-simple vertex shader for maximum compatibility with all WebGL versions
   * @returns {string} Vertex shader code
   */
  getUltraSimpleVertexShader() {
    return `
      uniform mat4 modelViewMatrix;
      uniform mat4 projectionMatrix;
      
      attribute vec3 position;
      attribute vec3 instancePosition;
      attribute float instanceHeight;
      attribute float instanceWidth;
      
      varying float vY;
      
      void main() {
        // Scale position by instance attributes
        vec3 pos = position;
        pos.x *= instanceWidth;
        pos.y *= instanceHeight;
        
        // Apply instance position
        pos += instancePosition;
        
        // Output position
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        
        // Pass y coord to fragment shader for coloring
        vY = position.y;
      }
    `;
  }

  /**
   * Get ultra-simple fragment shader for maximum compatibility with all WebGL versions
   * @returns {string} Fragment shader code
   */
  getUltraSimpleFragmentShader() {
    return `
      uniform vec3 baseColor;
      uniform vec3 tipColor;
      
      varying float vY;
      
      void main() {
        // Mix colors based on height
        vec3 color = mix(baseColor, tipColor, vY + 0.5);
        gl_FragColor = vec4(color, 1.0);
      }
    `;
  }

  /**
   * Get grass color based on biome
   * @param {string} biomeId - Biome ID
   * @returns {number[]} RGB color values (0-1)
   */
  getBiomeGrassColor(biomeId) {
    switch(biomeId) {
      case 'forest':
        return [0.2, 0.6, 0.1]; // Darker green
      case 'grassland':
        return [0.3, 0.7, 0.2]; // Bright green
      case 'beach':
        return [0.5, 0.6, 0.2]; // Yellow-green
      case 'rocky_terrain':
        return [0.4, 0.5, 0.2]; // Muted green
      case 'mountains':
        return [0.3, 0.5, 0.1]; // Dark muted green
      default:
        return [0.3, 0.7, 0.2]; // Default green
    }
  }
  
  /**
   * Update animation for grass and other animated elements
   * @param {number} deltaTime - Time in seconds since last update
   * @param {THREE.Camera} camera - Camera for LOD calculations
   * @param {THREE.Vector3} playerPosition - Optional player position for interaction
   * @param {number} playerStrength - Optional player interaction strength (0-1)
   */
  update(deltaTime, camera, playerPosition, playerStrength = 0) {
    // Update FPS monitoring if adaptive grass is enabled
    if (this.fpsMonitoring.enabled) {
      this.updateFpsMonitoring(deltaTime);
    }
    
    // Update grass shader systems
    if (this.grassShaderSystems.length > 0) {
      // Performance optimization: Only update grass every other frame when far away
      const shouldUpdateDetailedGrass = !this.frameCount || (this.frameCount % 2 === 0);
      
      // Update each grass system
      for (const grassSystem of this.grassShaderSystems) {
        const material = grassSystem.material;
        
        if (grassSystem.isAdvanced) {
          // Apply player interaction for advanced grass if available
          if (playerPosition && material.uniforms.playerPosition) {
            material.uniforms.playerPosition.value.copy(playerPosition);
            material.uniforms.playerStrength.value = playerStrength;
          }
          
          // Performance optimization: Skip non-essential updates when far from camera
          if (camera && shouldUpdateDetailedGrass) {
            const distanceToCamera = camera.position.distanceTo(new THREE.Vector3(0, 0, 0));
            
            // FIXED: Don't toggle visibility based on camera angle (keep grass visible from above)
            // Instead only use distance-based visibility for extreme distances
            if (distanceToCamera > 150) {
              // Very far - minimal updates and only hide in low quality mode
              grassSystem.mesh.visible = this.options.grassQuality !== 'low';
              if (material.uniforms.time) {
                material.uniforms.time.value += deltaTime * 0.1; // Very slow animation
              }
            } else {
              // All other distances - keep visible
              grassSystem.mesh.visible = true;
              
              // Adjust animation speed based on distance for performance
              if (material.uniforms.time) {
                if (distanceToCamera > 100) {
                  material.uniforms.time.value += deltaTime * 0.25; // Slower animation
                } else if (distanceToCamera > 50) {
                  material.uniforms.time.value += deltaTime * 0.5; // Half-speed animation
                } else {
                  material.uniforms.time.value += deltaTime; // Full speed animation
                }
              }
            }
          }
        } else {
          // Basic grass shader updates
          if (material.uniforms.time) {
            material.uniforms.time.value += deltaTime * 0.5;
          }
        }
        
        // Update camera position for LOD if available
        if (camera && material.uniforms.cameraPosition) {
          material.uniforms.cameraPosition.value.copy(camera.position);
        }
      }

      // Apply adaptive grass density adjustments if needed and enough delay has passed
      if (this.fpsMonitoring.enabled && this.fpsMonitoring.adaptiveDelay <= 0) {
        this.adjustGrassDensity();
      } else if (this.fpsMonitoring.adaptiveDelay > 0) {
        this.fpsMonitoring.adaptiveDelay--;
      }
    }
    
    // Debug visualization updates - only run when necessary
    if (this.options.debug && this.debugMode && this.vegetationObjects.length > 0) {
      // Scan vegetation objects for any new collision meshes that need to be tracked
      this.vegetationObjects.forEach(obj => {
        // Check if this is a collision mesh that's not already tracked
        if (obj.userData && (obj.userData.isCollider || obj.userData.isObstacle) && 
            !this.collisionMeshes.includes(obj)) {
          this.collisionMeshes.push(obj);
          
          // Apply debug material if in debug mode
          if (this.debugMode) {
            const debugMaterial = new THREE.MeshBasicMaterial({
              color: obj.userData.type?.includes('tree') ? 0x00ff00 : 0xff0000,
              wireframe: true,
              opacity: 0.5,
              transparent: true
            });
            
            if (Array.isArray(obj.material)) {
              obj.material = obj.material.map(() => debugMaterial);
            } else {
              obj.material = debugMaterial;
            }
          }
        }
        
        // Also check children if it's a group
        if (obj.children) {
          obj.children.forEach(child => {
            if (child.userData && (child.userData.isCollider || child.userData.isObstacle) && 
                !this.collisionMeshes.includes(child)) {
              this.collisionMeshes.push(child);
              
              // Apply debug material if in debug mode
              if (this.debugMode) {
                const debugMaterial = new THREE.MeshBasicMaterial({
                  color: child.userData.type?.includes('tree') ? 0x00ff00 : 0xff0000,
                  wireframe: true,
                  opacity: 0.5,
                  transparent: true
                });
                
                if (Array.isArray(child.material)) {
                  child.material = child.material.map(() => debugMaterial);
                } else {
                  child.material = debugMaterial;
                }
              }
            }
          });
        }
      });
    }
    
    // Increment frame counter for performance optimizations
    this.frameCount = (this.frameCount || 0) + 1;
    
    // Reset frame counter to prevent overflow
    if (this.frameCount > 1000000) {
      this.frameCount = 0;
    }
  }

  /**
   * Monitor FPS for adaptive grass density
   * @param {number} deltaTime - Time in seconds since last frame
   */
  updateFpsMonitoring(deltaTime) {
    this.fpsMonitoring.frameCount++;
    
    // Calculate FPS every 10 frames
    if (this.fpsMonitoring.frameCount % 10 === 0) {
      const currentTime = performance.now();
      
      if (this.fpsMonitoring.lastTime > 0) {
        const elapsedTime = currentTime - this.fpsMonitoring.lastTime;
        const currentFps = 10000 / elapsedTime; // 10 frames / elapsed ms * 1000
        
        // Add to history
        this.fpsMonitoring.history.push(currentFps);
        
        // Keep history at desired length
        if (this.fpsMonitoring.history.length > this.fpsMonitoring.historySize) {
          this.fpsMonitoring.history.shift();
        }
        
        // Calculate average FPS
        if (this.fpsMonitoring.history.length > 0) {
          const totalFps = this.fpsMonitoring.history.reduce((sum, fps) => sum + fps, 0);
          this.fpsMonitoring.fps = totalFps / this.fpsMonitoring.history.length;
        }
      }
      
      this.fpsMonitoring.lastTime = currentTime;
    }
  }

  /**
   * Adjust grass density based on current FPS
   */
  adjustGrassDensity() {
    if (!this.grassShaderSystems.length || !this.fpsMonitoring.enabled) return;
    
    const fps = this.fpsMonitoring.fps;
    let targetAdjustmentFactor = this.fpsMonitoring.adjustmentFactor;
    
    // Determine target adjustment factor based on FPS thresholds
    if (fps < FPS_THRESHOLDS.LOW) {
      // Low FPS - reduce grass significantly
      targetAdjustmentFactor = Math.max(0.3, targetAdjustmentFactor - 0.05);
    } else if (fps < FPS_THRESHOLDS.MEDIUM) {
      // Medium FPS - reduce grass moderately
      targetAdjustmentFactor = Math.max(0.5, targetAdjustmentFactor - 0.03);
    } else if (fps > FPS_THRESHOLDS.HIGH && fps < FPS_THRESHOLDS.TARGET) {
      // Good FPS but below target - gradually increase if below base
      if (targetAdjustmentFactor < 1.0) {
        targetAdjustmentFactor = Math.min(1.0, targetAdjustmentFactor + 0.01);
      }
    } else if (fps > FPS_THRESHOLDS.TARGET + 5) {
      // Exceeding target FPS by a good margin - we can add more grass
      targetAdjustmentFactor = Math.min(1.2, targetAdjustmentFactor + 0.005);
    }
    
    // Smoothly transition to target factor
    if (targetAdjustmentFactor !== this.fpsMonitoring.adjustmentFactor) {
      const diff = targetAdjustmentFactor - this.fpsMonitoring.adjustmentFactor;
      this.fpsMonitoring.adjustmentFactor += diff * this.fpsMonitoring.transitionSpeed;
      
      // Update grass instances for all grass systems
      this.updateGrassInstances();
      
      // Add delay before next adjustment to let FPS stabilize
      this.fpsMonitoring.adaptiveDelay = 30; // Wait 30 frames before next adjustment
      
      console.log(`Adaptive grass: FPS ${fps.toFixed(1)}, adjusting density to ${this.fpsMonitoring.adjustmentFactor.toFixed(2)}`);
    }
  }

  /**
   * Update grass instance count based on current adjustment factor
   */
  updateGrassInstances() {
    for (const grassSystem of this.grassShaderSystems) {
      if (!grassSystem.isAdvanced) continue;
      
      // Calculate new instance count
      const newInstanceCount = Math.floor(grassSystem.originalInstanceCount * this.fpsMonitoring.adjustmentFactor);
      
      // Only update if the count has changed significantly
      if (Math.abs(newInstanceCount - grassSystem.currentInstanceCount) > grassSystem.originalInstanceCount * 0.05) {
        // Update the instanced geometry with the new count
        grassSystem.geometry.instanceCount = newInstanceCount;
        grassSystem.currentInstanceCount = newInstanceCount;
      }
    }
  }

  /**
   * Clean up resources by disposing geometries and materials
   */
  dispose() {
    // Dispose of all vegetation objects
    this.vegetationObjects.forEach(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach(mat => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });
    
    // Dispose of instanced meshes
    Object.values(this.instancedMeshes).forEach(mesh => {
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(mat => mat.dispose());
        } else {
          mesh.material.dispose();
        }
      }
    });
    
    // Clear the arrays
    this.vegetationObjects = [];
    this.instancedMeshes = {};
    
    // Dispose of model geometries
    for (const type in this.models) {
      const model = this.models[type];
      if (model.geometry) {
        if (model.geometry instanceof THREE.Group) {
          model.geometry.children.forEach(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(mat => mat.dispose());
              } else {
                child.material.dispose();
              }
            }
          });
        } else if (model.geometry.geometry) {
          model.geometry.geometry.dispose();
        }
      }
      if (model.material) {
        if (Array.isArray(model.material)) {
          model.material.forEach(mat => mat.dispose());
        } else {
          model.material.dispose();
        }
      }
    }
    
    // Clear models
    this.models = {};
    
    // Clean up loaded models
    this.loadedModels = {};
    
    // Additional cleanup for shader grass
    if (this.grassShaderSystems) {
      this.grassShaderSystems = [];
    }
    
    // Clean up wind system
    if (this.windSystem) {
      this.windSystem = null;
    }
  }

  /**
   * Get simple vertex shader for maximum compatibility
   * @returns {string} Vertex shader code
   */
  getSimpleVertexShader() {
    return `
      // Three.js already provides these uniforms, so we don't declare them again
      // uniform mat4 modelViewMatrix;
      // uniform mat4 projectionMatrix;
      
      uniform float time;
      
      // Three.js already provides this attribute, so we don't declare it again
      // attribute vec3 position;
      
      attribute vec3 instancePosition;
      attribute float instanceHeight;
      attribute float instanceRotation;
      attribute float instanceColorVariation;
      attribute float instanceWidth;
      
      varying float vHeight;
      varying float vColorVariation;
      
      void main() {
        // Pass data to fragment shader
        vHeight = position.y + 0.5;
        vColorVariation = instanceColorVariation;
        
        // Position calculation
        vec3 pos = position;
        
        // Width scaling
        pos.x *= instanceWidth;
        
        // Height scaling
        pos.y *= instanceHeight;
        
        // Basic wind animation
        float wind = sin(time * 0.5 + instancePosition.x * 0.1) * 0.1;
        if (vHeight > 0.5) {
          pos.x += wind * vHeight;
        }
        
        // Simple rotation around Y
        float c = cos(instanceRotation);
        float s = sin(instanceRotation);
        vec3 rotPos = vec3(
          pos.x * c - pos.z * s,
          pos.y,
          pos.x * s + pos.z * c
        );
        
        // Final position
        vec3 worldPos = rotPos + instancePosition;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
      }
    `;
  }

  /**
   * Get simple fragment shader for maximum compatibility
   * @returns {string} Fragment shader code
   */
  getSimpleFragmentShader() {
    return `
      uniform vec3 baseColor;
      uniform vec3 tipColor;
      
      varying float vHeight;
      varying float vColorVariation;
      
      void main() {
        // Simple color blending
        vec3 baseAdjusted = baseColor * vColorVariation;
        vec3 tipAdjusted = tipColor * vColorVariation;
        vec3 color = mix(baseAdjusted, tipAdjusted, vHeight);
        gl_FragColor = vec4(color, 1.0);
      }
    `;
  }

  /**
   * Create collision objects for bushes
   * @param {Object[]} bushData - Array of bush positions, scales, and rotations
   * @param {string} vegetationType - Type of vegetation
   * @param {THREE.Scene} scene - Scene to add the collision objects to
   */
  createBushCollisionObjects(bushData, vegetationType, scene) {
    console.log(`Creating complex collision objects for ${bushData.length} ${vegetationType} instances`);
    
    // Check if we have a loaded model for this bush type
    const loadedModel = this.loadedModels[vegetationType];
    
    // Special handling for rock formations (BUSH_LARGE)
    if (vegetationType === 'BUSH_LARGE') {
      console.log(`Creating enhanced rock collision geometry for ${bushData.length} instances`);
      
      // Create rock collision meshes that better match the visual geometry
      bushData.forEach((bush, index) => {
        // Create a group to hold multiple collision boxes
        const collisionGroup = new THREE.Group();
        collisionGroup.position.copy(bush.position);
        collisionGroup.rotation.y = bush.rotation;
        
        // Mark the group with metadata
        collisionGroup.userData = {
          isObstacle: true,
          isCollider: true,
          type: 'rock-formation-collider',
          vegetationType: vegetationType,
          instanceIndex: index
        };
        
        // Calculate rock size based on scale
        const rockScale = bush.scale;
        const baseWidth = 5.0 * rockScale;
        const baseHeight = 2.5 * rockScale;
        const baseDepth = 5.0 * rockScale;
        
        // Create multiple overlapping collision boxes to approximate rock shape
        
        // Main rock body (center mass) - Red
        const centerCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.8,
          baseHeight * 0.5,
          baseDepth * 0.56,
          new THREE.Vector3(-baseWidth * 0.1, baseHeight * 0.3, 0),
          0xff0000 // Red
        );
        
        // Left section - Green
        const leftCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.25,
          baseHeight * 0.7,
          baseDepth * 0.15,
          new THREE.Vector3(-baseWidth * 0.3, baseHeight * 0.35, -baseDepth * 0.02),
          0x00ff00 // Green
        );
        
        // Right section - Blue
        const rightCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.25,
          baseHeight * 0.65,
          baseDepth * 0.2,
          new THREE.Vector3(baseWidth * 0.6, baseHeight * 0.25, baseDepth * 0.02),
          0x0000ff // Blue
        );
        
        // Top section - Yellow
        const topCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.35,
          baseHeight * 0.4,
          baseDepth * 0.2,
          new THREE.Vector3(baseWidth * 0.1, baseHeight * 0.6, baseDepth * 0.15),
          0xffff00 // Yellow
        );
        
        // Front extrusion - Cyan
        const frontCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.35,
          baseHeight * 0.8,
          baseDepth * 0.3,
          new THREE.Vector3(baseWidth * 0.2, baseHeight * 0.15, baseDepth * 0.25),
          0x00ffff // Cyan
        );
        
        // Back extrusion - Magenta
        const backCollision = this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.7,
          baseHeight * 0.35,
          baseDepth * 0.7,
          new THREE.Vector3(-baseWidth * 0.15, baseHeight * 0.175, 0),
          0xff00ff // Magenta
        );
        
        // Bottom left section - Orange
        this.addCollisionPartToGroup(
          collisionGroup,
          baseWidth * 0.6,
          baseHeight * 0.2,
          baseDepth * 0.6,
          new THREE.Vector3(-baseWidth * 0.1, baseHeight * 0.1, baseDepth * 0.05),
          0xff7700 // Orange
        );
        
        // Add to scene and track
        scene.add(collisionGroup);
        this.vegetationObjects.push(collisionGroup);
        
        // Debug log for rock collisions
       // console.log(`Rock formation collision group created with ${collisionGroup.children.length} collision boxes, isObstacle: ${collisionGroup.userData.isObstacle}`);
      });
      
      //console.log(`Created detailed rock collision objects for ${bushData.length} instances`);
      return;
    }
    
    // For other bush types, use the previously implemented approach
    if (loadedModel) {
      console.log(`Using multi-box collision for ${vegetationType}`);
    
      // Create multiple collision boxes that better approximate the complex shape
    bushData.forEach((bush, index) => {
        // Create a group to hold multiple collision boxes
        const collisionGroup = new THREE.Group();
        collisionGroup.position.copy(bush.position);
        collisionGroup.rotation.y = bush.rotation;
        
        // Mark the group with metadata
        collisionGroup.userData = {
          isObstacle: true,
          isCollider: true,
          type: 'bush-instance-collider-group',
          vegetationType: vegetationType,
          instanceIndex: index
        };
        
        // Get bounds of loaded model to create appropriate collision parts
        const modelBounds = new THREE.Box3().setFromObject(loadedModel);
        const modelSize = new THREE.Vector3();
        modelBounds.getSize(modelSize);
        
        // Scale bounds to match this instance
        const scaledWidth = modelSize.x * bush.scale;
        const scaledHeight = modelSize.y * bush.scale;
        const scaledDepth = modelSize.z * bush.scale;
        
        // Create collision parts based on the type of bush
        if (vegetationType === 'BUSH_SMALL') {
          // For shipping containers, create a more box-like shape with gaps
          // Main body
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.9,
            scaledHeight * 0.8,
            scaledDepth * 0.9,
            new THREE.Vector3(0, scaledHeight * 0.4, 0)
          );
          
          // Create some smaller collision boxes for details
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.3,
            scaledHeight * 0.2,
            scaledDepth * 0.3,
            new THREE.Vector3(scaledWidth * 0.3, scaledHeight * 0.9, 0)
          );
          
        } else if (vegetationType === 'BUSH_DESERT') {
          // For desert bushes, create a more rounded shape
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.7,
            scaledHeight * 0.7,
            scaledDepth * 0.7,
            new THREE.Vector3(0, scaledHeight * 0.35, 0)
          );
          
          // Add some smaller collision elements for foliage/branches
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.5,
            scaledHeight * 0.3,
            scaledDepth * 0.4,
            new THREE.Vector3(scaledWidth * 0.2, scaledHeight * 0.6, scaledDepth * 0.1)
          );
          
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.5,
            scaledHeight * 0.3,
            scaledDepth * 0.4,
            new THREE.Vector3(-scaledWidth * 0.2, scaledHeight * 0.65, -scaledDepth * 0.1)
          );
        } else {
          // Generic bush - create a more complex shape with multiple parts
          // Main body
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.9,
            scaledHeight * 0.7,
            scaledDepth * 0.9,
            new THREE.Vector3(0, scaledHeight * 0.35, 0)
          );
          
          // Top part
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.7,
            scaledHeight * 0.3,
            scaledDepth * 0.7,
            new THREE.Vector3(0, scaledHeight * 0.85, 0)
          );
          
          // Sides for more complex shape
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.4,
            scaledHeight * 0.4,
            scaledDepth * 0.4,
            new THREE.Vector3(scaledWidth * 0.3, scaledHeight * 0.4, 0)
          );
          
          this.addCollisionPartToGroup(
            collisionGroup,
            scaledWidth * 0.4,
            scaledHeight * 0.4,
            scaledDepth * 0.4,
            new THREE.Vector3(-scaledWidth * 0.3, scaledHeight * 0.4, 0)
          );
        }
        
        // Add the collision group to the scene
        scene.add(collisionGroup);
        this.vegetationObjects.push(collisionGroup);
      });
      
    } else {
      console.log(`No loaded model for ${vegetationType}, using default collision shape`);
      
      // Fallback to simpler collision boxes if no model is available
      bushData.forEach((bush, index) => {
        // Use hardcoded dimensions scaled by bush scale
        const bushScale = bush.scale;
        const width = 1 * bushScale;
        const height = 3.5 * bushScale;
        const depth = 7.2 * bushScale;
      
      // Create collision box for the bush
      const collisionGeometry = new THREE.BoxGeometry(
        width,
        height,
        depth
      );
      
        // Material depends on debug mode
      const collisionMaterial = new THREE.MeshBasicMaterial({
          visible: this.debugMode,
          wireframe: this.debugMode,
          color: 0xff0000,
        transparent: true,
          opacity: this.debugMode ? 0.5 : 0
      });
      
      const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
      
      // Position the collision mesh
      collisionMesh.position.copy(bush.position);
      collisionMesh.position.y += height / 2;
      collisionMesh.rotation.y = bush.rotation;
      
      // Add metadata
      collisionMesh.userData = {
        isObstacle: true,
        isCollider: true,
        type: 'bush-instance-collider',
        vegetationType: vegetationType,
        instanceIndex: index
      };
      
      // Ensure it has a bounding box
      collisionGeometry.computeBoundingBox();
      
      // Add to scene and track it
      scene.add(collisionMesh);
      this.vegetationObjects.push(collisionMesh);
        this.collisionMeshes.push(collisionMesh); // Track for debug visualizations
      });
    }
    
    console.log(`Created collision objects for ${bushData.length} ${vegetationType} instances`);
  }
  
  /**
   * Helper method to add a collision part to a collision group
   * @param {THREE.Group} group - The group to add the collision part to
   * @param {number} width - Width of the collision box
   * @param {number} height - Height of the collision box
   * @param {number} depth - Depth of the collision box
   * @param {THREE.Vector3} position - Position relative to the group
   * @returns {THREE.Mesh} The created collision part
   */
  addCollisionPartToGroup(group, width, height, depth, position, color = 0xff0000) {
    const collisionGeometry = new THREE.BoxGeometry(width, height, depth);
    
    // Material depends on debug mode
    const collisionMaterial = new THREE.MeshBasicMaterial({
      visible: this.debugMode,
      wireframe: this.debugMode,
      color: color,
      transparent: true,
      opacity: this.debugMode ? 0.5 : 0
    });
    
    const collisionPart = new THREE.Mesh(collisionGeometry, collisionMaterial);
    collisionPart.position.copy(position);
    
    // Store original debug color for later use
    collisionPart.userData = {
      isObstacle: true,
      isCollider: true,
      type: 'bush-collision-part',
      debugColor: color
    };
    
    // Ensure it has a bounding box
    collisionGeometry.computeBoundingBox();
    
    // Add to the group
    group.add(collisionPart);
    
    // Track for debug visualizations
    this.collisionMeshes.push(collisionPart);
    
    return collisionPart;
  }

  /**
   * Set up keyboard listener for toggling debug mode
   */
  setupDebugKeyListener() {
    // Use D key by default for debug mode
    window.addEventListener('keydown', (event) => {
      // Check if the key pressed is 'B' (for bounding box)
      if (event.key === 'b' || event.key === 'B') {
        // Toggle debug mode
        this.toggleDebugMode();
      }
    });

    console.log('Debug mode keyboard listener set up. Press "B" to toggle collision visualization.');
  }

  /**
   * Toggle debug mode to show/hide collision meshes
   */
  toggleDebugMode() {
    // Toggle the debug mode state
    this.debugMode = !this.debugMode;
    
    // Update visibility of all collision meshes
    this.updateCollisionVisibility();
    
    console.log(`Collision visualization ${this.debugMode ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Set debug mode state
   * @param {boolean} enabled - Whether to enable debug mode
   */
  setDebugMode(enabled) {
    if (this.debugMode !== enabled) {
      this.debugMode = enabled;
      this.updateCollisionVisibility();
    }
  }
  
  /**
   * Update visibility of all collision meshes based on debug mode
   */
  updateCollisionVisibility() {
    // Update all collision meshes
    this.collisionMeshes.forEach(collisionMesh => {
      if (this.debugMode) {
        // Create debug material with the mesh's custom color if available
        const debugColor = collisionMesh.userData && collisionMesh.userData.debugColor 
          ? collisionMesh.userData.debugColor 
          : 0xff0000;
          
        const debugMaterial = new THREE.MeshBasicMaterial({
          color: debugColor,
          wireframe: true,
          opacity: 0.5,
          transparent: true
        });
        
        // Change to wireframe material in debug mode
        if (Array.isArray(collisionMesh.material)) {
          collisionMesh.material = collisionMesh.material.map(() => debugMaterial);
        } else {
          collisionMesh.material = debugMaterial;
        }
      } else {
        // Invisible material for normal mode
        const invisibleMaterial = new THREE.MeshBasicMaterial({
          visible: false,
          transparent: true,
          opacity: 0
        });
        
        // Change back to invisible material
        if (Array.isArray(collisionMesh.material)) {
          collisionMesh.material = collisionMesh.material.map(() => invisibleMaterial);
        } else {
          collisionMesh.material = invisibleMaterial;
        }
      }
    });
  }

  /**
   * Create a detailed collision mesh based on the actual model geometry
   * @param {THREE.Object3D} model - The loaded model to extract geometry from
   * @param {number} scale - Scale factor to apply
   * @param {THREE.Vector3} position - Position where to place the collision mesh
   * @param {number} rotation - Y-axis rotation
   * @returns {THREE.Object3D} The collision object
   */
  createDetailedCollisionMesh(model, scale, position, rotation) {
    // Create a group to hold the collision meshes
    const collisionGroup = new THREE.Group();
    collisionGroup.position.copy(position);
    collisionGroup.rotation.y = rotation;
    
    // Set collision metadata
    collisionGroup.userData = {
      isObstacle: true,
      isCollider: true,
      type: 'detailed-collision-mesh'
    };

    // If we don't have a model, return an empty group
    if (!model) {
      console.warn('No model provided for detailed collision mesh creation');
      return collisionGroup;
    }

    // Get the bounding box of the model to determine overall dimensions
    const modelBounds = new THREE.Box3().setFromObject(model);
    const modelSize = new THREE.Vector3();
    modelBounds.getSize(modelSize);
    
    // Clone the model for collision detection
    const clonedModel = model.clone();
    
    // Apply scaling to match the instance size
    clonedModel.scale.set(scale, scale, scale);
    
    // Counter for tracking geometry complexity
    let totalVertices = 0;
    let totalParts = 0;
    
    // Process each mesh in the model
    clonedModel.traverse(child => {
      if (child.isMesh) {
        totalParts++;
        
        // Create a collision mesh based on the simplified geometry
        const originalGeometry = child.geometry;
        
        // Simplify the geometry for collision detection
        const collisionGeometry = this.simplifyCollisionGeometry(originalGeometry, 0.7);
        
        // Track complexity stats
        if (originalGeometry.attributes && originalGeometry.attributes.position) {
          const originalVertices = originalGeometry.attributes.position.count;
          const simplifiedVertices = collisionGeometry.attributes.position.count;
          totalVertices += simplifiedVertices;
          
          // Log if this is a particularly complex part
          if (originalVertices > 1000) {
            console.log(`Simplified complex geometry part from ${originalVertices} to ${simplifiedVertices} vertices`);
          }
        }
        
        // Create an invisible or debug material for the collision mesh
        const collisionMaterial = new THREE.MeshBasicMaterial({
          visible: this.debugMode, 
          wireframe: this.debugMode,
          color: 0xff0000,
          transparent: true,
          opacity: this.debugMode ? 0.5 : 0
        });
        
        // Create the collision mesh
        const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
        
        // Copy the transform from the original mesh
        collisionMesh.position.copy(child.position);
        collisionMesh.rotation.copy(child.rotation);
        collisionMesh.scale.copy(child.scale);
        
        // Set collision metadata
        collisionMesh.userData = {
          isObstacle: true,
          isCollider: true,
          type: 'model-part-collider',
          debugColor: 0xff0000
        };
        
        // Add the collision mesh to the group
        collisionGroup.add(collisionMesh);
        
        // Track for debug visualization
        this.collisionMeshes.push(collisionMesh);
      }
    });
    
    // Log summary of collision mesh creation
    if (totalParts > 0) {
      console.log(`Created detailed collision mesh with ${totalParts} parts and ${totalVertices} total vertices`);
    }
    
    return collisionGroup;
  }

  /**
   * Simplify a geometry for collision purposes
   * @param {THREE.BufferGeometry} geometry - The geometry to simplify
   * @param {number} targetReduction - Target reduction percentage (0-1)
   * @returns {THREE.BufferGeometry} The simplified geometry
   */
  simplifyCollisionGeometry(geometry, targetReduction = 0.7) {
    // First, make sure we have our utility
    if (!this.bufferGeometryUtils) {
      this.importThreeBufferGeometryUtils();
    }
    
    // Use our local buffer geometry utils
    const mergedGeometry = this.bufferGeometryUtils.mergeVertices(geometry.clone());
    
    console.log(`Simplified collision geometry from ${geometry.attributes.position.count} to ${
      mergedGeometry.attributes.position.count} vertices`);
    
    return mergedGeometry;
  }

  /**
   * Initialize the GLTFLoader for models
   * @private
   */
  async _initializeLoader() {
    if (GLTFLoader) return;
    
    // Check if GLTFLoader is already available globally (from dynamic import)
    if (window.GLTFLoader) {
      GLTFLoader = window.GLTFLoader;
    } else {
      // Try to import it directly
      try {
        const module = await import('three/examples/jsm/loaders/GLTFLoader.js');
        GLTFLoader = module.GLTFLoader;
      } catch (error) {
        console.error('Failed to load GLTFLoader:', error);
        throw error;
      }
    }
  }

  /**
   * Load a 3D model
   * @param {string} path - Path to the GLTF/GLB model
   * @returns {Promise<THREE.Object3D>} - The loaded model
   * @private
   */
  async _loadModel(path) {
    // Initialize the loader if needed
    await this._initializeLoader();
    
    // Return from cache if available
    if (this.modelCache[path]) {
      return this.modelCache[path].clone();
    }
    
    // Create the loader if not already created
    if (!this.gltfLoader) {
      // Ensure GLTFLoader is available 
      if (typeof GLTFLoader === 'undefined' && typeof window.GLTFLoader !== 'undefined') {
        // Use the global GLTFLoader if available
        this.gltfLoader = new window.GLTFLoader();
      } else if (typeof GLTFLoader !== 'undefined') {
        // Use the module-level GLTFLoader if available
        this.gltfLoader = new GLTFLoader();
      } else {
        throw new Error('GLTFLoader is not available');
      }
    }
    
    return new Promise((resolve, reject) => {
      this.gltfLoader.load(
        path,
        (gltf) => {
          const model = gltf.scene;
          
          // Cache the model for future use
          this.modelCache[path] = model;
          
          // Resolve with a clone to prevent modifications to the cached version
          resolve(model.clone());
        },
        (xhr) => {
          // Loading progress callback
          const percentComplete = (xhr.loaded / xhr.total) * 100;
          console.log(`Loading model ${path}: ${Math.round(percentComplete)}%`);
        },
        (error) => {
          console.error('Error loading model:', error);
          reject(error);
        }
      );
    });
  }

  /**
   * Add a collision mesh to a vegetation object
   * @param {THREE.Object3D} object - The object to add collision to
   * @param {number} width - Width of the collision box
   * @param {number} height - Height of the collision box
   * @param {number} depth - Depth of the collision box
   * @param {THREE.Vector3} position - Position offset
   */
  addCollisionMesh(object, width, height, depth, position = new THREE.Vector3(0, 0, 0)) {
    // Create a simplified collision box
    const collisionGeometry = new THREE.BoxGeometry(width, height, depth);
    
    // Use an invisible material for the collision mesh
    const collisionMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      transparent: true,
      opacity: 0
    });
    
    const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
    collisionMesh.position.copy(position);
    
    // Mark as collision-only object for tree
    collisionMesh.userData = {
      isObstacle: true,
      isCollider: true,
      type: 'tree-collider'
    };
    
    // Ensure it has a bounding box
    collisionGeometry.computeBoundingBox();
    
    // Add to object
    object.add(collisionMesh);
    
    return collisionMesh;
  }
}

export default VegetationSystem; 